import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { useAdmin } from '../context/AdminContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { sanitizeHtml } from '../utils/securityUtils';
import Sidebar from '../components/Sidebar';
import SpinningWheel from '../components/SpinningWheel';
import AvailableProducts from '../components/AvailableProducts';

import { adminFetchInit } from '../utils/adminToken';

// Streamer Management Component
const StreamerManagement = ({ t }) => {
  const [streamers, setStreamers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockModal, setBlockModal] = useState({ isOpen: false, streamer: null });
  const [blockReason, setBlockReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState(null);
  const [unblockModal, setUnblockModal] = useState({ isOpen: false, streamer: null });

  // Fetch approved streamers
  const fetchStreamers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/get-applications', adminFetchInit());
      if (response.ok) {
        const result = await response.json();
        const managedStreamers = (result.data || []).filter(
          app => app.status === 'approved' || app.status === 'blocked'
        );
        setStreamers(managedStreamers);
      } else {
        throw new Error('Failed to fetch applications');
      }
    } catch (error) {
      console.error('Error fetching streamers:', error);
      showToast(t('errorLoadingApplications').replace('{message}', error.message), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStreamers();
  }, [fetchStreamers]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handleBlockClick = (streamer) => {
    setBlockModal({ isOpen: true, streamer });
    setBlockReason('');
  };

  const handleCloseModal = () => {
    setBlockModal({ isOpen: false, streamer: null });
    setBlockReason('');
  };

  const handleBlockStreamer = async () => {
    if (!blockReason.trim()) {
      showToast(t('blockReasonRequired'), 'error');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch('/.netlify/functions/manage-streamer-status', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          applicationId: blockModal.streamer.id,
          action: 'block',
          reason: blockReason
        })
      }));

      if (response.ok) {
        showToast(t('streamerBlockedSuccess'), 'success');
        handleCloseModal();
        fetchStreamers(); // Refresh the list
      } else {
        throw new Error('Failed to block streamer');
      }
    } catch (error) {
      console.error('Error blocking streamer:', error);
      showToast(t('errorBlockingStreamer'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnblockStreamer = (streamer) => {
    setUnblockModal({ isOpen: true, streamer });
  };

  const confirmUnblockStreamer = async () => {
    const streamer = unblockModal.streamer;
    if (!streamer) return;

    setUnblockModal({ isOpen: false, streamer: null });
    setProcessing(true);
    try {
      const response = await fetch('/.netlify/functions/manage-streamer-status', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          applicationId: streamer.id,
          action: 'unblock'
        })
      }));

      if (response.ok) {
        showToast(t('streamerUnblockedSuccess'), 'success');
        fetchStreamers();
      } else {
        throw new Error('Failed to unblock streamer');
      }
    } catch (error) {
      console.error('Error unblocking streamer:', error);
      showToast(t('errorUnblockingStreamer'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        <span className="ml-3 text-gray-300">{t('loadingStreamers')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-700 rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-2">{t('userManagementTitle')}</h3>
        <p className="text-gray-300 text-sm">{t('userManagementFeatureComingSoon')}</p>
      </div>

      {/* Streamers Table */}
      {streamers.length === 0 ? (
        <div className="bg-gray-700 rounded-xl p-6 text-center">
          <p className="text-gray-400">{t('noStreamersFound')}</p>
        </div>
      ) : (
        <div className="bg-gray-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {t('streamerNameLabel')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {t('streamerEmail')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {t('accountStatus')}
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {t('actionsColumn')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {streamers.map((streamer) => (
                  <tr key={streamer.id} className="hover:bg-gray-600 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{streamer.streamer}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{streamer.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        streamer.status === 'blocked'
                          ? 'bg-red-900 text-red-200'
                          : 'bg-green-900 text-green-200'
                      }`}>
                        {streamer.status === 'blocked' ? t('blocked') : t('activeStatus')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {streamer.status === 'blocked' ? (
                        <button
                          onClick={() => handleUnblockStreamer(streamer)}
                          disabled={processing}
                          className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                          </svg>
                          {t('unblockStreamer')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleBlockClick(streamer)}
                          disabled={processing}
                          className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          {t('blockStreamer')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Block Modal */}
      {blockModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1100] p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4">
              {t('blockStreamer')}
            </h3>
            <p className="text-gray-300 mb-4">
              {t('blockingStreamer')} <span className="font-semibold text-white">{blockModal.streamer?.streamer}</span>
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('blockReason')} <span className="text-red-400">*</span>
              </label>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder={t('blockReasonPlaceholder')}
                rows="4"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleBlockStreamer}
                disabled={processing || !blockReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Processing...
                  </>
                ) : (
                  t('confirmBlock')
                )}
              </button>
              <button
                onClick={handleCloseModal}
                disabled={processing}
                className="flex-1 bg-gray-600 hover:bg-gray-500 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50"
              >
                {t('cancelAction')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unblock Confirmation Modal */}
      {unblockModal.isOpen && unblockModal.streamer && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1100] p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-2xl border border-gray-600">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Unblock Streamer</h3>
                <p className="text-gray-400 text-sm">This will restore their access</p>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-gray-300 mb-3">
                Are you sure you want to unblock this streamer?
              </p>
              <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                <p className="text-white font-medium">{unblockModal.streamer.streamer}</p>
                <p className="text-gray-400 text-sm mt-1">{unblockModal.streamer.email || 'No email'}</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setUnblockModal({ isOpen: false, streamer: null })}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmUnblockStreamer}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                Unblock
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-5 right-5 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center ${
          toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        } text-white max-w-md animate-fadeIn`}>
          <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {toast.type === 'error' ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            )}
          </svg>
          <span className="font-medium">{toast.message}</span>
        </div>
      )}
    </div>
  );
};

//stest
// Security function to sanitize user input and prevent XSS
const sanitizeText = (text) => {
  if (!text) return '';
  
  // Convert to string and remove any HTML tags and scripts
  const cleanText = String(text)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '[BLOCKED SCRIPT]')
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocols
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trim();
    
  return cleanText;
};

// Additional security for displaying user content safely
const SecureText = ({ children, className = '', title = '' }) => {
  const sanitizedText = sanitizeText(children);
  const sanitizedTitle = sanitizeText(title);
  
  return (
    <span 
      className={className} 
      title={sanitizedTitle}
      dangerouslySetInnerHTML={undefined} // Prevent any HTML injection
    >
      {sanitizedText}
    </span>
  );
};

// Dynamic Text Component with Smart Language Detection and Free Google Translate API
const DynamicText = ({ text }) => {
  const { currentLanguage } = useLanguage();
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const translateWithFreeGoogleAPI = async () => {
      // If no text, don't show anything
      if (!text) {
        setIsReady(true);
        return;
      }

      // SMART LANGUAGE DETECTION - Detect content language first
      const detectContentLanguage = (text) => {
        // Enhanced French detection patterns
        const frenchIndicators = [
          ' le ', ' la ', ' les ', ' du ', ' de la ', ' des ', ' un ', ' une ',
          ' est ', ' sont ', ' avoir ', ' être ', ' faire ', ' aller ', ' venir ',
          ' avec ', ' pour ', ' dans ', ' sur ', ' par ', ' sans ', ' sous ',
          ' nous ', ' vous ', ' ils ', ' elles ', ' qui ', ' que ', ' dont ',
          ' mais ', ' ou ', ' et ', ' donc ', ' car ', ' ni ', ' or ',
          ' ce ', ' cette ', ' ces ', ' cet ', ' mon ', ' ma ', ' mes ',
          'tion ', 'sion ', 'ment ', 'ique ', 'able ', 'ible '
        ];
        
        const englishIndicators = [
          ' the ', ' and ', ' or ', ' but ', ' with ', ' from ', ' to ',
          ' a ', ' an ', ' this ', ' that ', ' these ', ' those ',
          ' is ', ' are ', ' was ', ' were ', ' have ', ' has ', ' had ',
          ' will ', ' would ', ' can ', ' could ', ' should ', ' must ',
          ' of ', ' in ', ' on ', ' at ', ' by ', ' for '
        ];
        
        const lowerText = text.toLowerCase();
        let frenchScore = 0;
        let englishScore = 0;
        
        // Count French indicators
        frenchIndicators.forEach(indicator => {
          const matches = (lowerText.match(new RegExp(indicator.replace(/\s/g, '\\s'), 'g')) || []).length;
          frenchScore += matches;
        });
        
        // Count English indicators
        englishIndicators.forEach(indicator => {
          const matches = (lowerText.match(new RegExp(indicator.replace(/\s/g, '\\s'), 'g')) || []).length;
          englishScore += matches;
        });
        
        // Return detected language
        if (frenchScore > englishScore) {
          return 'fr';
        } else if (englishScore > frenchScore) {
          return 'en';
        }
        
        // If tied or no clear indicators, check character patterns
        const frenchChars = (lowerText.match(/[àâäéèêëïîôöùûüÿç]/g) || []).length;
        if (frenchChars > 0) {
          return 'fr';
        }
        
        // Default to English if no clear pattern
        return 'en';
      };
      
      const detectedContentLanguage = detectContentLanguage(text);
      const sidebarLanguage = currentLanguage;
      
      // SMART LOGIC: Only translate if languages are different
      if (detectedContentLanguage === sidebarLanguage) {
        // Languages match! Content is already in correct language - show directly
        setTranslatedText(text);
        setIsReady(true);
        return;
      }
      
      // Languages differ! Start translation
      setIsTranslating(true);
      setIsReady(false);

      // Add timeout to prevent infinite loading
      const translationTimeout = setTimeout(() => {
        console.warn('Translation timeout reached, showing original text');
        setTranslatedText(text);
        setIsTranslating(false);
        setIsReady(true);
      }, 10000); // 10 second timeout

      try {
        // Check if text contains HTML tags
        const hasHtml = /<[^>]+>/.test(text);
        
        if (hasHtml) {
          // For HTML content, skip translation and use original HTML
          // This preserves the HTML structure and formatting
          console.log('HTML content detected, skipping translation to preserve HTML structure');
          clearTimeout(translationTimeout);
          setTranslatedText(text);
        } else {
          // For plain text, use normal translation
          const response = await fetch('/.netlify/functions/translate-free', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              text: text,
              from: detectedContentLanguage,
              to: sidebarLanguage
            })
          });

          const result = await response.json();
          console.log('Free Google Translate result:', result);

          if (result && result.success && result.translatedText) {
            clearTimeout(translationTimeout);
            setTranslatedText(result.translatedText);
          } else {
            // Fallback to original text if translation fails
            console.warn('Translation result format unexpected:', result);
            clearTimeout(translationTimeout);
            setTranslatedText(text);
          }
        }
      } catch (error) {
        console.error('Free Google Translate API failed:', error);
        clearTimeout(translationTimeout);
        setTranslatedText(text);
      } finally {
        setIsTranslating(false);
        setIsReady(true);
      }
    };

    translateWithFreeGoogleAPI();
  }, [text, currentLanguage]);

  // Show only loader while translating (no text)
  if (isTranslating) {
    return (
      <div className="flex items-center justify-center py-2">
        <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show translated text only when ready
  if (isReady && translatedText) {
    const sanitizedHtml = sanitizeHtml(translatedText);
    return (
      <div 
        className="transition-all duration-300 ease-in-out"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    );
  }

  // Show nothing while waiting
  return null;
};

// Multilingual Text Component for Admin Dashboard
const MultilingualText = ({ text, fieldType = 'message', truncate = false, maxLength = 150 }) => {
  // Helper function to check if text is multilingual (object/array)
  const isMultilingualContent = (content) => {
    if (!content) return false;
    
    // Check if it's an array
    if (Array.isArray(content)) return true;
    
    // Check if it's an object
    if (typeof content === 'object' && content !== null) return true;
    
    // Check if it's a JSON string that parses to object/array
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return typeof parsed === 'object' && parsed !== null;
      } catch (e) {
        return false;
      }
    }
    
    return false;
  };

  // Helper function to truncate text content
  const truncateContent = (content) => {
    if (!truncate || !content) return content;
    
    // Check if content contains HTML
    const hasHtml = /<[^>]+>/.test(content);
    
    if (hasHtml) {
      // For HTML content, strip tags for length calculation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      
      if (textContent.length <= maxLength) return content;
      
      // Return truncated text content (without HTML for preview)
      return textContent.substring(0, maxLength).trim() + '...';
    }
    
    // For plain text, use simple truncation
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  // If multilingual content, display with flags
  if (isMultilingualContent(text)) {
    let parsedContent = text;
    
    // Parse JSON string if needed
    if (typeof text === 'string') {
      try {
        parsedContent = JSON.parse(text);
      } catch (e) {
        return <DynamicText text={text} />;
      }
    }
    
    // Handle array case (return first element)
    if (Array.isArray(parsedContent)) {
      const content = parsedContent.length > 0 ? parsedContent[0] : '';
      return <DynamicText text={truncateContent(content)} />;
    }
    
    // Handle object case - display both languages with flags
    if (typeof parsedContent === 'object' && parsedContent !== null) {
      return (
        <div className="space-y-3">
          {/* English Version */}
          {parsedContent.english && (
            <div className="border-l-2 border-blue-400 pl-3">
              <div className="flex items-center mb-1">
                <span className="fi fi-us mr-2"></span>
                <span className="text-blue-400 font-medium text-xs">EN</span>
              </div>
              <div 
                className="text-gray-200 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: truncateContent(parsedContent.english) 
                }}
              />
            </div>
          )}
          
          {/* French Version */}
          {parsedContent.french && (
            <div className="border-l-2 border-red-400 pl-3">
              <div className="flex items-center mb-1">
                <span className="fi fi-fr mr-2"></span>
                <span className="text-red-400 font-medium text-xs">FR</span>
              </div>
              <div 
                className="text-gray-200 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: truncateContent(parsedContent.french) 
                }}
              />
            </div>
          )}
        </div>
      );
    }
  }
  
  // For simple text, use smart language detection and translation
  return <DynamicText text={truncateContent(text)} />;
};

const AdminDashboardOwner = () => {
  const { admin, isAuthenticated, isLoading } = useAdmin();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Country mapping for flags (from home.js)
  const countryMapping = {
    'France': 'FR', 'france': 'FR', 'España': 'ES', 'Spain': 'ES', 'spain': 'ES',
    'United States': 'US', 'united states': 'US', 'USA': 'US', 'usa': 'US',
    'United Kingdom': 'GB', 'united kingdom': 'GB', 'UK': 'GB', 'uk': 'GB',
    'Deutschland': 'DE', 'deutschland': 'DE', 'Germany': 'DE', 'germany': 'DE',
    'Italia': 'IT', 'italia': 'IT', 'Italy': 'IT', 'italy': 'IT',
    'Portugal': 'PT', 'portugal': 'PT', 'Canada': 'CA', 'canada': 'CA',
    'Australia': 'AU', 'australia': 'AU', 'Brasil': 'BR', 'brasil': 'BR',
    'Brazil': 'BR', 'brazil': 'BR', 'México': 'MX', 'mexico': 'MX', 'Mexico': 'MX',
    'Argentina': 'AR', 'argentina': 'AR', 'Colombia': 'CO', 'colombia': 'CO',
    'Chile': 'CL', 'chile': 'CL', 'Perú': 'PE', 'peru': 'PE', 'Peru': 'PE',
    '日本': 'JP', 'Japan': 'JP', 'japan': 'JP', '한국': 'KR', 'Korea': 'KR', 'korea': 'KR',
    'South Korea': 'KR', 'south korea': 'KR', '中国': 'CN', 'China': 'CN', 'china': 'CN',
    'India': 'IN', 'india': 'IN', 'Россия': 'RU', 'Russia': 'RU', 'russia': 'RU',
    'Nederland': 'NL', 'netherlands': 'NL', 'Netherlands': 'NL', 'België': 'BE',
    'belgium': 'BE', 'Belgium': 'BE', 'Schweiz': 'CH', 'switzerland': 'CH',
    'Switzerland': 'CH', 'Österreich': 'AT', 'austria': 'AT', 'Austria': 'AT',
    'Philippines': 'PH', 'philippines': 'PH'
  };

  // Get country code from country name
  const getCountryCode = (countryName) => {
    if (!countryName) return null;
    const trimmedName = countryName.trim();
    return countryMapping[trimmedName] || 
           countryMapping[trimmedName.toLowerCase()] || 
           countryMapping[trimmedName.charAt(0).toUpperCase() + trimmedName.slice(1).toLowerCase()];
  };

  // Get flag CSS class for flag-icons
  const getFlagClass = (countryCode) => {
    if (!countryCode || countryCode.length !== 2) {
      return null;
    }
    const code = countryCode.toLowerCase();
    return `fi fi-${code}`;
  };

  // Get flag display for a country
  const getFlagDisplay = (countryName) => {
    const countryCode = getCountryCode(countryName);
    if (countryCode) {
      const flagClass = getFlagClass(countryCode);
      return { flagClass, countryCode };
    }
    return { flagClass: null, countryCode: null };
  };
  
  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'users', 'analytics', 'applications', 'suggestions', 'image-manager', 'top-designs', 'top-votes', 'draw'].includes(tabParam)) {
      return tabParam;
    }
    return 'overview';
  };

  const getInitialWebsite = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('website') || '';
  };

  // Ref to track internal navigation changes to prevent race conditions
  const isInternalNavigationRef = useRef(false);

  // State declarations first
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSidebarItem, setActiveSidebarItem] = useState('dashboard');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [selectedWebsite, setSelectedWebsite] = useState(getInitialWebsite());
  const [uploads, setUploads] = useState([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(12);
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Top Designs state
  const [topDesigns, setTopDesigns] = useState([]);
  const [loadingTopDesigns, setLoadingTopDesigns] = useState(false);
  const [topDesignsWebsiteFilter, setTopDesignsWebsiteFilter] = useState('all');
  const [topDesignsDiscordFilter, setTopDesignsDiscordFilter] = useState('all'); // 'all', 'with-discord', 'without-discord'
  
  // Top Votes state
  const [topVotes, setTopVotes] = useState([]);
  const [loadingTopVotes, setLoadingTopVotes] = useState(false);
  const [topVotesWebsiteFilter, setTopVotesWebsiteFilter] = useState('all');
  const [topVotesFromDate, setTopVotesFromDate] = useState('2026-01-02T05:00');
  const [topVotesToDate, setTopVotesToDate] = useState('2026-01-04T18:00');
  
  // Draw state
  const [drawParticipants, setDrawParticipants] = useState([]);
  const [loadingDrawParticipants, setLoadingDrawParticipants] = useState(false);
  const [drawFromDate, setDrawFromDate] = useState(() => {
    // Set to start of today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().slice(0, 16);
  });
  const [drawToDate, setDrawToDate] = useState(() => {
    // Set to end of today
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today.toISOString().slice(0, 16);
  });
  const [showDrawPriceModal, setShowDrawPriceModal] = useState(false);
  const [drawPrice, setDrawPrice] = useState('');
  const [isSavingDrawPrice, setIsSavingDrawPrice] = useState(false);
  const [drawPrices, setDrawPrices] = useState([]);
  const [loadingDrawPrices, setLoadingDrawPrices] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');
  
  // Spinning wheel states
  const [showSpinningWheel, setShowSpinningWheel] = useState(false);
  const [activePrizes, setActivePrizes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [isExecutingDraw, setIsExecutingDraw] = useState(false);
  const [drawWinner, setDrawWinner] = useState(null);
  const [drawError, setDrawError] = useState(null); // Error message when draw fails - allows draw again
  const [drawType, setDrawType] = useState('vote'); // 'vote' | 'upload' - which draw when wheel is open

  // Draw nested tab: Vote (current) | Upload (new)
  const [drawSubTab, setDrawSubTab] = useState('vote');

  // Draw drop zone filter: array of selected websites (empty = all drop zones)
  const [drawDropZoneFilter, setDrawDropZoneFilter] = useState([]);

  // Upload draw state (participants from upload table status=1)
  const [drawUploadParticipants, setDrawUploadParticipants] = useState([]);
  const [loadingDrawUploadParticipants, setLoadingDrawUploadParticipants] = useState(false);
  const [drawUploadFromDate, setDrawUploadFromDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().slice(0, 16);
  });
  const [drawUploadToDate, setDrawUploadToDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today.toISOString().slice(0, 16);
  });
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const spinningWheelRef = useRef(null);
  
  // Winners modal states
  const [showWinnersModal, setShowWinnersModal] = useState(false);
  const [winners, setWinners] = useState([]);
  const [loadingWinners, setLoadingWinners] = useState(false);
  const [winnersSubTab, setWinnersSubTab] = useState('vote'); // 'vote' | 'upload' - filter by draw type
  
  const [selectedUploads, setSelectedUploads] = useState(new Set());
  const [fadingOutUploads, setFadingOutUploads] = useState(new Set());
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [uploadToApprove, setUploadToApprove] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [uploadToReject, setUploadToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Blur image state
  const [blurReasonSelected, setBlurReasonSelected] = useState({}); // Track selected blur reason per upload
  const [blurModalUploadId, setBlurModalUploadId] = useState(null); // Track which upload's blur modal is open
  const [selectedImageBlur, setSelectedImageBlur] = useState(null); // Track blur reason for selected image in modal
  const [revealBlurredImage, setRevealBlurredImage] = useState(false); // Track if user clicked to reveal blurred image
  
  // Applications state
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [applicationsFilter, setApplicationsFilter] = useState('all'); // all, pending, approved, rejected
  const [emailSearch, setEmailSearch] = useState('');
  const [socialFilter, setSocialFilter] = useState('');
  const [subscribersMin, setSubscribersMin] = useState('');
  const [subscribersMax, setSubscribersMax] = useState('');

  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsPage, setSuggestionsPage] = useState(1);
  const [suggestionsPagination, setSuggestionsPagination] = useState(null);
  const [processingApplication, setProcessingApplication] = useState(null);
  
  // Image Manager state — start in loading when landing on this tab to avoid empty-state flash
  const [allImages, setAllImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const tab = new URLSearchParams(window.location.search).get('tab');
      return tab === 'image-manager';
    } catch {
      return false;
    }
  });
  const [imageStatusFilter, setImageStatusFilter] = useState('all'); // all, 0 (pending), 1 (approved), 2 (rejected), 9 (pending AKS)
  const [imageWebsiteFilter, setImageWebsiteFilter] = useState('');
  const [availableWebsites, setAvailableWebsites] = useState([]);
  const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deletingImage, setDeletingImage] = useState(false);
  // Image popup state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Image carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  
  // Image view dropdown state (profile or logo)
  const [imageViewMode, setImageViewMode] = useState({}); // Track view mode per application
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  
  // Text popup states
  const [showVisionModal, setShowVisionModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [selectedVision, setSelectedVision] = useState('');
  const [selectedAbout, setSelectedAbout] = useState('');

  // Predefined rejection reasons
  const rejectionReasons = [
    { display: 'Invalid image content', message: 'Invalid image content BY: admin' },
    { display: 'Image is not clear', message: 'Image is not clear BY: admin' },
    { display: 'Image is from other site', message: 'Image is from other site BY: admin' }
  ];

  // Predefined blur reasons (always in English)
  const blurReasons = [
    'This image has been blurred because it contains sensitive or private personal information.',
    'This image has been blurred to protect the identity of individuals shown.',
    'This image has been blurred due to graphic or disturbing content.',
    'This image has been blurred because it includes confidential or restricted material.',
    'This image has been blurred to comply with safety and content guidelines.',
    'This image has been blurred because it may not be appropriate for all audiences.',
    'This image has been blurred because it features a minor (under 18), and displaying it clearly would violate safety and content standards.'
  ];

  // Define all useCallback functions after state declarations
  const getInitialSidebarItem = useCallback((tab) => {
    switch (tab) {
      case 'overview':
        return 'dashboard';
      case 'users':
        return 'users';
      case 'analytics':
        return 'analytics';
      case 'applications':
        return 'applications';
      default:
        return 'dashboard';
    }
  }, []);

  // Toast function
  const showToastMessage = useCallback((message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  }, []);

  // Handle rejection cancellation
  const cancelRejection = useCallback(() => {
    setShowRejectionModal(false);
    setUploadToReject(null);
    setRejectionReason('');
  }, []);

  // Handle approval cancellation
  const cancelApproval = useCallback(() => {
    setShowApprovalModal(false);
    setUploadToApprove(null);
  }, []);

  // Handle upload approval
  const handleApproveUpload = useCallback(async (uploadId, isBlurred = false, blurReasonText = '') => {
    if (!selectedWebsite) {
      showToastMessage(t('noWebsiteSelected'), 'error');
      return;
    }

    // Start fade-out animation
    setFadingOutUploads(prev => new Set([...prev, uploadId]));

    try {
      // Status is always 0 for approval (blur reason is stored in blur field)
      const status = 0;
      console.log(`🟢 Approving upload ${uploadId} for website ${selectedWebsite}, isBlurred: ${isBlurred}, status: ${status}`);
      
      const requestBody = {
        uploadId: uploadId,
        status: status, // 0=pending streamers, 1=approved, 2=rejected, 3=deleted, 9=pending admin
        website: selectedWebsite,
        adminEmail: admin?.email || 'admin'
      };
      
      // Add blur reason directly to blur field if blurred
      if (isBlurred && blurReasonText) {
        requestBody.blur = blurReasonText;
      }
      
      const response = await fetch('/.netlify/functions/admin-update-upload', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(requestBody)
      }));

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Upload approved successfully:', data);
        
        showToastMessage(isBlurred ? t('imageBlurredSuccess') : t('uploadSentForReview'), 'success');
        
        // Clear blur reason state for this upload
        setBlurReasonSelected(prev => {
          const newState = { ...prev };
          delete newState[uploadId];
          return newState;
        });
        
        // Remove from local state after fade animation (800ms)
        setTimeout(() => {
          setUploads(prevUploads => prevUploads.filter(upload => upload.id !== uploadId));
          setFadingOutUploads(prev => {
            const newSet = new Set(prev);
            newSet.delete(uploadId);
            return newSet;
          });
          setSelectedUploads(prev => {
            const newSet = new Set(prev);
            newSet.delete(uploadId);
            return newSet;
          });
        }, 800);
        
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to approve upload:', errorData);
        showToastMessage(t('errorApproving').replace('{error}', errorData.error || t('unknownError')), 'error');
        
        // Remove fade-out state on error
        setFadingOutUploads(prev => {
          const newSet = new Set(prev);
          newSet.delete(uploadId);
          return newSet;
        });
      }
    } catch (error) {
      console.error('❌ Network error approving upload:', error);
      showToastMessage(t('networkErrorApproving'), 'error');
      
      // Remove fade-out state on error
      setFadingOutUploads(prev => {
        const newSet = new Set(prev);
        newSet.delete(uploadId);
        return newSet;
      });
    }
  }, [selectedWebsite, admin, showToastMessage, t]);

  // Handle approval confirmation
  const confirmApproval = useCallback(async () => {
    if (!uploadToApprove) return;
    setShowApprovalModal(false);
    await handleApproveUpload(uploadToApprove.id);
    setUploadToApprove(null);
  }, [uploadToApprove, handleApproveUpload]);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    console.log('🚀 fetchApplications function called!');
    setLoadingApplications(true);
    try {
      console.log('🔍 Fetching applications...');
      
      // Build query parameters
      const params = new URLSearchParams();
      if (applicationsFilter !== 'all') {
        params.append('status', applicationsFilter);
      }
      if (emailSearch.trim()) {
        params.append('email', emailSearch.trim());
      }
      if (socialFilter.trim()) {
        params.append('social', socialFilter.trim());
      }
      if (subscribersMin.trim()) {
        params.append('subscribersMin', subscribersMin.trim());
      }
      if (subscribersMax.trim()) {
        params.append('subscribersMax', subscribersMax.trim());
      }
      params.append('limit', '100');
      params.append('sortBy', 'submitted_at');
      params.append('sortOrder', 'desc');
      
      const queryString = params.toString();
      const url = `/.netlify/functions/get-applications${queryString ? `?${queryString}` : ''}`;
      
      console.log('📡 Fetching from:', url);
      
      const response = await fetch(url, adminFetchInit());
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Applications fetched successfully:', data.data.length);
        setApplications(data.data || []);
      } else {
        console.error('❌ Failed to fetch applications:', data.message);
        showToastMessage(t('errorLoadingApplications').replace('{message}', data.message), 'error');
        setApplications([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching applications:', error);
      showToastMessage(t('networkErrorLoadingApplications'), 'error');
      setApplications([]);
    } finally {
      setLoadingApplications(false);
    }
  }, [applicationsFilter, emailSearch, socialFilter, subscribersMin, subscribersMax, showToastMessage, t]);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async () => {
    console.log('🚀 fetchSuggestions function called!');
    setLoadingSuggestions(true);
    try {
      console.log('🔍 Fetching suggestions...');
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', suggestionsPage.toString());
      params.append('limit', '20');
      params.append('sortBy', 'created_at');
      params.append('sortOrder', 'desc');
      
      const queryString = params.toString();
      const url = `/.netlify/functions/get-suggestions?${queryString}`;
      
      console.log('📡 Fetching suggestions from:', url);
      
      const response = await fetch(url, adminFetchInit());
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Suggestions fetched successfully:', data.data.length);
        setSuggestions(data.data || []);
        setSuggestionsPagination(data.pagination);
      } else {
        console.error('❌ Failed to fetch suggestions:', data.message);
        showToastMessage(t('errorLoadingSuggestions').replace('{message}', data.message), 'error');
        setSuggestions([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching suggestions:', error);
      showToastMessage(t('networkErrorLoadingSuggestions'), 'error');
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [suggestionsPage, showToastMessage, t]);

  // Fetch all images for Image Manager
  const fetchAllImages = useCallback(async () => {
    console.log('🚀 fetchAllImages function called!');
    setLoadingImages(true);
    try {
      console.log('🔍 Fetching all images...');
      
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add status filter if not 'all'
      if (imageStatusFilter !== 'all') {
        params.append('status', imageStatusFilter);
      }
      
      // Add website filter if specified (add .com back for query)
      if (imageWebsiteFilter) {
        const websiteWithDomain = imageWebsiteFilter.includes('.com') ? imageWebsiteFilter : `${imageWebsiteFilter}.com`;
        params.append('website', websiteWithDomain);
      }
      
      const queryString = params.toString();
      const url = `/.netlify/functions/get-upload-data${queryString ? '?' + queryString : ''}`;
      
      console.log('📡 Fetching images from:', url);
      
      const response = await fetch(url, adminFetchInit());
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Images fetched successfully:', data.data.length);
        
        let images = data.data || [];
        
        // Extract unique websites for dropdown (only if not filtering by website)
        if (!imageWebsiteFilter) {
          const websites = [...new Set(images.map(img => {
            if (!img.website) return null;
            // Remove .com extension for display
            return img.website.replace('.com', '');
          }).filter(Boolean))].sort();
          setAvailableWebsites(websites);
        }
        
        // Sort by website field
        const sortedImages = images.sort((a, b) => {
          const websiteA = a.website || '';
          const websiteB = b.website || '';
          return websiteA.localeCompare(websiteB);
        });
        
        setAllImages(sortedImages);
      } else {
        console.error('❌ Failed to fetch images:', data.message);
        showToastMessage(t('errorLoadingImages') || 'Error loading images', 'error');
        setAllImages([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching images:', error);
      showToastMessage(t('networkErrorLoadingImages') || 'Network error loading images', 'error');
      setAllImages([]);
    } finally {
      setLoadingImages(false);
    }
  }, [imageStatusFilter, imageWebsiteFilter, showToastMessage, t]);

  // Handle delete image click
  const handleDeleteImageClick = useCallback((image) => {
    setImageToDelete(image);
    setShowDeleteImageModal(true);
  }, []);

  // Cancel delete
  const cancelDeleteImage = useCallback(() => {
    setShowDeleteImageModal(false);
    setImageToDelete(null);
  }, []);

  // Confirm delete
  const confirmDeleteImage = useCallback(async () => {
    if (!imageToDelete) return;

    setDeletingImage(true);
    try {
      console.log('🗑️ Deleting image:', imageToDelete.id);
      
      const response = await fetch('/.netlify/functions/delete-upload', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          uploadId: imageToDelete.id
        })
      }));

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('✅ Image deleted successfully');
        showToastMessage(t('uploadDeletedSuccess'), 'success');
        
        // Remove from local state
        setAllImages(prev => prev.filter(img => img.id !== imageToDelete.id));
        
        // Close modal
        setShowDeleteImageModal(false);
        setImageToDelete(null);
      } else {
        console.error('❌ Failed to delete image:', result);
        showToastMessage(result.message || t('errorDeletingImage'), 'error');
      }
    } catch (error) {
      console.error('❌ Error deleting image:', error);
      showToastMessage(t('errorDeletingImage'), 'error');
    } finally {
      setDeletingImage(false);
    }
  }, [imageToDelete, showToastMessage, t]);

  // Handle application approval
  const handleApproveApplication = useCallback(async (applicationId) => {
    console.log('🎯 Approving application:', applicationId);
    setProcessingApplication(applicationId);
    
    try {
      const response = await fetch('/.netlify/functions/approve-application', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          applicationId: applicationId
        })
      }));

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('✅ Application approved successfully');
        showToastMessage(t('applicationApprovedSuccess'), 'success');
        
        // Remove from local state
        setApplications(prev => prev.filter(app => app.id !== applicationId));
        
        // Refresh website dropdown after successful approval
        if (window.refreshWebsiteList) {
          console.log('🔄 Refreshing website list after approval');
          window.refreshWebsiteList();
        }
      } else {
        console.error('❌ Failed to approve application:', result);
        showToastMessage(t('errorApproving').replace('{error}', result.error || t('unknownError')), 'error');
      }
    } catch (error) {
      console.error('❌ Error approving application:', error);
      showToastMessage(t('networkErrorApproving'), 'error');
    } finally {
      setProcessingApplication(null);
    }
  }, [showToastMessage, t]);

  // Handle application dismissal
  const handleDismissApplication = useCallback(async (applicationId) => {
    console.log('❌ Dismissing application:', applicationId);
    setProcessingApplication(applicationId);
    
    try {
      const response = await fetch('/.netlify/functions/dismiss-application', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          applicationId: applicationId
        })
      }));

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('✅ Application dismissed successfully');
        showToastMessage(t('applicationRejectedSuccess'), 'success');
        
        // Remove from local state
        setApplications(prev => prev.filter(app => app.id !== applicationId));
      } else {
        console.error('❌ Failed to dismiss application:', result);
        showToastMessage(t('errorRejecting').replace('{error}', result.error || t('unknownError')), 'error');
      }
    } catch (error) {
      console.error('❌ Error dismissing application:', error);
      showToastMessage(t('networkErrorRejecting'), 'error');
    } finally {
      setProcessingApplication(null);
    }
  }, [showToastMessage, t]);

  // Handle modal keyboard events
  const handleKeyDown = useCallback((event) => {
    if (showApprovalModal) {
      if (event.key === 'Escape') {
        cancelApproval();
      } else if (event.key === 'Enter') {
        confirmApproval();
      }
    } else if (showRejectionModal) {
      if (event.key === 'Escape') {
        cancelRejection();
      }
      // Don't auto-confirm on Enter for rejection as user needs to select reason
    }
  }, [showApprovalModal, showRejectionModal, confirmApproval, cancelRejection, cancelApproval]);

  // Fetch uploads for selected website
  const fetchWebsiteUploads = useCallback(async (website) => {
    if (!website) {
      console.log('No website selected, skipping fetch');
      setUploads([]);
      return;
    }
    
    setLoadingUploads(true);
    try {
      const response = await fetch(`/.netlify/functions/get-website-uploads?website=${website}`, adminFetchInit());
      const result = await response.json();
      
      if (result.success) {
        setUploads(result.data || []);
      } else {
        console.error('Failed to fetch uploads:', result.message);
        setUploads([]);
      }
    } catch (error) {
      console.error('Error fetching uploads:', error);
      setUploads([]);
    } finally {
      setLoadingUploads(false);
    }
  }, []);
  
  // Fetch top designs
  const fetchTopDesigns = useCallback(async () => {
    setLoadingTopDesigns(true);
    try {
      const response = await fetch('/.netlify/functions/get-top-designs', adminFetchInit());
      const result = await response.json();
      
      if (result.success) {
        setTopDesigns(result.data || []);
      } else {
        console.error('Failed to fetch top designs:', result.message);
        setTopDesigns([]);
      }
    } catch (error) {
      console.error('Error fetching top designs:', error);
      setTopDesigns([]);
    } finally {
      setLoadingTopDesigns(false);
    }
  }, []);
  
  // Fetch top votes
  const fetchTopVotes = useCallback(async () => {
    setLoadingTopVotes(true);
    try {
      // Build query parameters for backend filtering
      const params = new URLSearchParams();
      
      if (topVotesWebsiteFilter && topVotesWebsiteFilter !== 'all') {
        params.append('streamer', topVotesWebsiteFilter);
      }
      
      if (topVotesFromDate) {
        params.append('from_date', topVotesFromDate);
      }
      
      if (topVotesToDate) {
        params.append('to_date', topVotesToDate);
      }
      
      const url = `/.netlify/functions/get-top-voters${params.toString() ? '?' + params.toString() : ''}`;
      console.log('Fetching top voters with URL:', url);
      
      const response = await fetch(url, adminFetchInit());
      const result = await response.json();
      
      if (result.success) {
        setTopVotes(result.data || []);
      } else {
        console.error('Failed to fetch top voters:', result.message);
        setTopVotes([]);
      }
    } catch (error) {
      console.error('Error fetching top voters:', error);
      setTopVotes([]);
    } finally {
      setLoadingTopVotes(false);
    }
  }, [topVotesWebsiteFilter, topVotesFromDate, topVotesToDate]);

  // Fetch draw participants from vote_logs joined with user table
  const fetchDrawParticipants = useCallback(async () => {
    setLoadingDrawParticipants(true);
    try {
      const params = new URLSearchParams();
      
      if (drawFromDate) {
        params.append('from_date', drawFromDate);
      }
      
      if (drawToDate) {
        params.append('to_date', drawToDate);
      }
      
      if (drawDropZoneFilter && drawDropZoneFilter.length > 0) {
        params.append('website', drawDropZoneFilter.join(','));
      }
      
      const response = await fetch(`/.netlify/functions/get-draw-participants?${params.toString()}`, adminFetchInit());
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Draw participants fetched:', result.data);
        setDrawParticipants(result.data || []);
      } else {
        console.error('Failed to fetch draw participants:', result.message);
        setDrawParticipants([]);
      }
    } catch (error) {
      console.error('Error fetching draw participants:', error);
      setDrawParticipants([]);
    } finally {
      setLoadingDrawParticipants(false);
    }
  }, [drawFromDate, drawToDate, drawDropZoneFilter]);

  // Fetch upload draw participants (from upload table status=1, joined with user)
  const fetchDrawUploadParticipants = useCallback(async () => {
    setLoadingDrawUploadParticipants(true);
    try {
      const params = new URLSearchParams();
      if (drawUploadFromDate) params.append('from_date', drawUploadFromDate);
      if (drawUploadToDate) params.append('to_date', drawUploadToDate);
      if (drawDropZoneFilter && drawDropZoneFilter.length > 0) {
        params.append('website', drawDropZoneFilter.join(','));
      }
      const response = await fetch(`/.netlify/functions/get-draw-participants-upload?${params.toString()}`, adminFetchInit());
      const result = await response.json();
      if (result.success) {
        setDrawUploadParticipants(result.data || []);
      } else {
        setDrawUploadParticipants([]);
      }
    } catch (error) {
      console.error('Error fetching upload draw participants:', error);
      setDrawUploadParticipants([]);
    } finally {
      setLoadingDrawUploadParticipants(false);
    }
  }, [drawUploadFromDate, drawUploadToDate, drawDropZoneFilter]);

  // Save draw price
  const handleSaveDrawPrice = async () => {
    if (!drawPrice || drawPrice.trim() === '') {
      showToastMessage('Please enter a prize amount', 'error');
      return;
    }

    setIsSavingDrawPrice(true);
    try {
      const response = await fetch('/.netlify/functions/save-draw-price', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          price: drawPrice.trim()
        })
      }));

      const result = await response.json();

      if (result.success) {
        showToastMessage('Draw price saved successfully!', 'success');
        setDrawPrice('');
        fetchDrawPrices(); // ✅ Refresh the list after adding
      } else {
        showToastMessage(result.message || 'Failed to save draw price', 'error');
      }
    } catch (error) {
      console.error('Error saving draw price:', error);
      showToastMessage('Error saving draw price', 'error');
    } finally {
      setIsSavingDrawPrice(false);
    }
  };

  // Fetch draw prices
  const fetchDrawPrices = async () => {
    setLoadingDrawPrices(true);
    try {
      const response = await fetch('/.netlify/functions/get-draw-prices', adminFetchInit());
      const result = await response.json();
      
      if (result.success) {
        setDrawPrices(result.data || []);
        return result.data || [];
      } else {
        console.error('Failed to fetch draw prices:', result.message);
        setDrawPrices([]);
        return [];
      }
    } catch (error) {
      console.error('Error fetching draw prices:', error);
      setDrawPrices([]);
      return [];
    } finally {
      setLoadingDrawPrices(false);
    }
  };

  // Update draw price
  const handleUpdateDrawPrice = async (id, price) => {
    try {
      const response = await fetch('/.netlify/functions/update-draw-price', adminFetchInit({
        method: 'PUT',
        body: JSON.stringify({ id, price })
      }));

      const result = await response.json();

      if (result.success) {
        showToastMessage('Draw price updated successfully!', 'success');
        setEditingPriceId(null);
        setEditingPriceValue('');
        fetchDrawPrices(); // Refresh list
      } else {
        showToastMessage(result.message || 'Failed to update draw price', 'error');
      }
    } catch (error) {
      console.error('Error updating draw price:', error);
      showToastMessage('Error updating draw price', 'error');
    }
  };

  // Toggle draw price status
  const handleToggleDrawPriceStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      
      const response = await fetch('/.netlify/functions/update-draw-price', adminFetchInit({
        method: 'PUT',
        body: JSON.stringify({ id, status: newStatus })
      }));

      const result = await response.json();

      if (result.success) {
        showToastMessage(`Status changed to ${newStatus === 1 ? 'Active' : 'Inactive'}`, 'success');
        fetchDrawPrices(); // Refresh list
      } else {
        showToastMessage(result.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      showToastMessage('Error toggling status', 'error');
    }
  };

  // Fetch winners
  const fetchWinners = async () => {
    setLoadingWinners(true);
    try {
      const response = await fetch('/.netlify/functions/get-winners', adminFetchInit());
      const result = await response.json();
      
      if (result.success) {
        setWinners(result.data || []);
      } else {
        console.error('Failed to fetch winners:', result.message);
        setWinners([]);
        showToastMessage('Failed to load winners', 'error');
      }
    } catch (error) {
      console.error('Error fetching winners:', error);
      setWinners([]);
      showToastMessage('Error loading winners', 'error');
    } finally {
      setLoadingWinners(false);
    }
  };

  // Open winners modal
  const handleOpenWinnersModal = () => {
    setShowWinnersModal(true);
    setWinnersSubTab('vote');
    fetchWinners();
  };

  // Filter winners by type (vote | upload)
  const voteWinners = winners.filter(w => w.from === 'vote');
  const uploadWinners = winners.filter(w => w.from === 'upload');
  const displayedWinners = winnersSubTab === 'upload' ? uploadWinners : voteWinners;

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Redirect if not admin
  useEffect(() => {
    if (!isLoading && isAuthenticated && admin?.owner !== 'admin') {
      console.log('🚫 Non-admin user trying to access admin dashboard, redirecting to streamer dashboard');
      navigate('/streamer/dashboard');
    }
  }, [admin, isAuthenticated, isLoading, navigate]);

  // URL sync effect - with race condition prevention
  useEffect(() => {
    // If this is an internal navigation change, reset the flag and skip sync
    if (isInternalNavigationRef.current) {
      isInternalNavigationRef.current = false;
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const websiteParam = searchParams.get('website');
    
    // Handle tab parameter
    if (
      tabParam &&
      [
        'overview',
        'users',
        'analytics',
        'applications',
        'suggestions',
        'image-manager',
        'top-designs',
        'top-votes',
        'draw'
      ].includes(tabParam)
    ) {
      setActiveTab(tabParam);
      setActiveSidebarItem(getInitialSidebarItem(tabParam));
    } else if (!tabParam) {
      setActiveTab('overview');
      setActiveSidebarItem('dashboard');
    }
    
    // Handle website parameter - only update if actually different
    if (websiteParam !== selectedWebsite) {
      if (websiteParam) {
        setSelectedWebsite(websiteParam);
        fetchWebsiteUploads(websiteParam);
      } else {
        // Clear selected website when no website parameter in URL
        setSelectedWebsite('');
        setUploads([]); // Clear uploads when no website selected
      }
    }
  }, [location.search, selectedWebsite, fetchWebsiteUploads, getInitialSidebarItem]);

  // Fetch applications when applications tab is active
  useEffect(() => {
    console.log('🔍 Applications useEffect triggered:', { activeTab, applicationsFilter });
    if (activeTab === 'applications') {
      console.log('✅ Calling fetchApplications...');
      fetchApplications();
    }
  }, [activeTab, applicationsFilter, emailSearch, socialFilter, subscribersMin, subscribersMax, fetchApplications]);

  // Fetch suggestions when suggestions tab is active
  useEffect(() => {
    console.log('🔍 Suggestions useEffect triggered:', { activeTab, suggestionsPage });
    if (activeTab === 'suggestions') {
      console.log('✅ Calling fetchSuggestions...');
      fetchSuggestions();
    }
  }, [activeTab, suggestionsPage, fetchSuggestions]);
  
  // Fetch top designs when top-designs tab is active
  useEffect(() => {
    if (activeTab === 'top-designs') {
      fetchTopDesigns();
      
      // Also fetch websites list if not already loaded
      if (availableWebsites.length === 0) {
        fetch('/.netlify/functions/get-upload-data', adminFetchInit())
          .then(res => res.json())
          .then(data => {
            if (data.success) {
              const websites = [...new Set((data.data || []).map(img => {
                if (!img.website) return null;
                return img.website;
              }).filter(Boolean))].sort();
              setAvailableWebsites(websites);
            }
          })
          .catch(err => console.error('Error fetching websites:', err));
      }
    }
  }, [activeTab, topDesignsWebsiteFilter, topDesignsDiscordFilter, fetchTopDesigns, availableWebsites.length]);
  
  // Fetch top votes when top-votes tab is active
  useEffect(() => {
    if (activeTab === 'top-votes') {
      fetchTopVotes();
      
      // Fetch websites/streamers from upload table
      fetch('/.netlify/functions/get-upload-data', adminFetchInit())
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            // Extract unique websites from uploads
            const websites = [...new Set((data.data || []).map(img => {
              if (!img.website) return null;
              return img.website;
            }).filter(Boolean))].sort();
            
            setAvailableWebsites(websites);
          }
        })
        .catch(err => console.error('Error fetching streamers:', err));
    }
  }, [activeTab, fetchTopVotes]);

  // Fetch draw participants when draw tab is active
  useEffect(() => {
    if (activeTab === 'draw') {
      fetchDrawParticipants();
    }
  }, [activeTab, drawFromDate, drawToDate, drawDropZoneFilter, fetchDrawParticipants]);

  // Fetch upload draw participants when draw tab + upload subtab is active
  useEffect(() => {
    if (activeTab === 'draw' && drawSubTab === 'upload') {
      fetchDrawUploadParticipants();
    }
  }, [activeTab, drawSubTab, drawUploadFromDate, drawUploadToDate, drawDropZoneFilter, fetchDrawUploadParticipants]);

  // Fetch available websites when draw tab is active (for drop zone filter)
  useEffect(() => {
    if (activeTab === 'draw') {
      // Fetch with higher limit to get all websites (including less recent ones like Allkeyshop)
      fetch('/.netlify/functions/get-upload-data?limit=5000', adminFetchInit())
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            const seen = new Set();
            const websites = (data.data || [])
              .map(img => img?.website)
              .filter(Boolean)
              .filter(w => {
                const key = String(w).trim().replace(/\.com$/i, '').toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              })
              .sort((a, b) => String(a).localeCompare(b));
            setAvailableWebsites(websites);
          }
        })
        .catch(err => console.error('Error fetching streamers for draw:', err));
    }
  }, [activeTab]);

  // Fetch draw prices when modal opens
  useEffect(() => {
    if (showDrawPriceModal) {
      fetchDrawPrices();
    }
  }, [showDrawPriceModal]);

  // Filter active prizes when draw prices change
  useEffect(() => {
    const active = drawPrices.filter(p => p.status === 1);
    setActivePrizes(active);
  }, [drawPrices]);

  // Handle spinning wheel completion
  const handleSpinComplete = async (prize) => {
    console.log('🎯 Spin complete! Selected prize:', prize);
    setSelectedPrize(prize);

    const participants = drawType === 'upload' ? drawUploadParticipants : drawParticipants;

    setTimeout(async () => {
      if (participants.length === 0) {
        showToastMessage('No participants available!', 'error');
        return;
      }

      const randomIndex = Math.floor(Math.random() * participants.length);
      const winner = participants[randomIndex];

      console.log('🎉 Random winner selected:', winner);

      if (drawType === 'upload') {
        await executeDrawUpload(winner.email, prize.id);
      } else {
        await executeDraw(winner.email, prize.id);
      }
    }, 1000);
  };

  // Execute upload draw - insert into draw with upload_id
  const executeDrawUpload = async (winnerEmail, prizeId) => {
    setIsExecutingDraw(true);
    try {
      const response = await fetch('/.netlify/functions/execute-draw-upload', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          winner_email: winnerEmail,
          prize_id: prizeId,
          from_date: drawUploadFromDate,
          to_date: drawUploadToDate,
          website: drawDropZoneFilter && drawDropZoneFilter.length > 0 ? drawDropZoneFilter.join(',') : null
        })
      }));
      const result = await response.json();
      if (result.success) {
        setDrawWinner(result.winner);
        setDrawError(null);
        setIsGeneratingVideo(true);
        showToastMessage(`🎉 Winner: ${result.winner.name || result.winner.username || result.winner.email}!`, 'success');
      } else {
        setDrawError(result.message || 'Failed to execute draw');
        showToastMessage(result.message || 'Failed to execute draw', 'error');
        spinningWheelRef.current?.stopRecording?.();
      }
    } catch (error) {
      setDrawError('Error executing draw');
      console.error('Error executing upload draw:', error);
      showToastMessage('Error executing draw', 'error');
      spinningWheelRef.current?.stopRecording?.();
    } finally {
      setIsExecutingDraw(false);
    }
  };

  // Execute draw - update winner's vote_logs to draw = true
  const executeDraw = async (winnerEmail, prizeId) => {
    setIsExecutingDraw(true);
    
    try {
      const response = await fetch('/.netlify/functions/execute-draw', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          winner_email: winnerEmail,
          prize_id: prizeId,
          from_date: drawFromDate,
          to_date: drawToDate,
          website: drawDropZoneFilter && drawDropZoneFilter.length > 0 ? drawDropZoneFilter.join(',') : null
        })
      }));

      const result = await response.json();

      if (result.success) {
        setDrawWinner(result.winner);
        setDrawError(null);
        setIsGeneratingVideo(true); // Start showing loader for video generation
        showToastMessage(`🎉 Winner: ${result.winner.name || result.winner.username || result.winner.email}!`, 'success');
        
        // Do NOT auto-close - user will click the close button
      } else {
        setDrawError(result.message || 'Failed to execute draw');
        showToastMessage(result.message || 'Failed to execute draw', 'error');
        spinningWheelRef.current?.stopRecording?.();
      }
    } catch (error) {
      setDrawError('Error executing draw');
      console.error('Error executing draw:', error);
      showToastMessage('Error executing draw', 'error');
      spinningWheelRef.current?.stopRecording?.();
    } finally {
      setIsExecutingDraw(false);
    }
  };

  // Open spinning wheel modal (vote draw)
  const handleOpenSpinningWheel = async () => {
    if (drawParticipants.length === 0) {
      showToastMessage('No participants available for draw!', 'error');
      return;
    }
    const prices = await fetchDrawPrices();
    const active = prices.filter(p => p.status === 1);
    if (active.length === 0) {
      showToastMessage('Please add at least one active prize first!', 'error');
      setShowDrawPriceModal(true);
      return;
    }
    setDrawType('vote');
    setActivePrizes(active);
    setShowSpinningWheel(true);
    setSelectedPrize(null);
    setDrawWinner(null);
    setDrawError(null);
  };

  // Open spinning wheel modal (upload draw)
  const handleOpenSpinningWheelUpload = async () => {
    if (drawUploadParticipants.length === 0) {
      showToastMessage('No participants available for draw!', 'error');
      return;
    }
    const prices = await fetchDrawPrices();
    const active = prices.filter(p => p.status === 1);
    if (active.length === 0) {
      showToastMessage('Please add at least one active prize first!', 'error');
      setShowDrawPriceModal(true);
      return;
    }
    setDrawType('upload');
    setActivePrizes(active);
    setShowSpinningWheel(true);
    setSelectedPrize(null);
    setDrawWinner(null);
    setDrawError(null);
  };

  // Show loading before paint when switching to Image Manager (avoids one frame of "no images" flash)
  useLayoutEffect(() => {
    if (activeTab === 'image-manager') {
      setLoadingImages(true);
    }
  }, [activeTab]);

  // Fetch images when image-manager tab is active (do not depend on availableWebsites.length — that retriggers after fetch and causes a second load + blink)
  useEffect(() => {
    if (activeTab === 'image-manager') {
      fetchAllImages();
    }
  }, [activeTab, imageStatusFilter, imageWebsiteFilter, fetchAllImages]);

  // Re-fetch when display settings change
  useEffect(() => {
    if (selectedWebsite) {
      fetchWebsiteUploads(selectedWebsite);
    }
  }, [displayLimit, sortOrder, selectedWebsite, fetchWebsiteUploads]);

  // Keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Handle approval modal effect
  useEffect(() => {
    if (uploadToApprove) {
      confirmApproval();
    }
  }, [uploadToApprove, confirmApproval]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Sync sidebar active state with active tab
  useEffect(() => {
    switch (activeTab) {
      case 'overview':
        setActiveSidebarItem('dashboard');
        break;
      case 'users':
        setActiveSidebarItem('users');
        break;
      case 'analytics':
        setActiveSidebarItem('analytics');
        break;
      case 'applications':
        setActiveSidebarItem('applications');
        break;
      case 'image-manager':
        setActiveSidebarItem('dashboard'); // or create a new sidebar item if needed
        break;
      default:
        setActiveSidebarItem('dashboard');
    }
  }, [activeTab]);

  // Handle tab changes with URL updates
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Set flag to prevent URL sync race condition
    isInternalNavigationRef.current = true;
    navigate(`/admin/dashboard?tab=${tab}`);
  };

  // Handle website selection from sidebar
  const handleWebsiteSelect = (website) => {
    setSelectedWebsite(website);
    setSelectedUploads(new Set()); // Clear selections when changing website
    
    // Update URL with website parameter
    const searchParams = new URLSearchParams(location.search);
    if (website) {
      searchParams.set('website', website);
    } else {
      searchParams.delete('website');
    }
    // Keep existing tab parameter
    if (!searchParams.get('tab')) {
      searchParams.set('tab', 'overview');
    }
    
    // Set flag to prevent URL sync race condition
    isInternalNavigationRef.current = true;
    navigate(`/admin/dashboard?${searchParams.toString()}`);
    fetchWebsiteUploads(website);
  };

  // Handle display limit change
  const handleDisplayLimitChange = (newLimit) => {
    setDisplayLimit(newLimit);
    if (selectedWebsite) {
      fetchWebsiteUploads(selectedWebsite);
    }
  };

  // Handle sort order change
  const handleSortOrderChange = (newOrder) => {
    setSortOrder(newOrder);
    if (selectedWebsite) {
      fetchWebsiteUploads(selectedWebsite);
    }
  };

  // Handle click outside modal to close
  const handleModalBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      if (showApprovalModal) {
        cancelApproval();
      } else if (showRejectionModal) {
        cancelRejection();
      }
    }
  };

  // Handle upload rejection
  const handleRejectUpload = async (uploadId, reason) => {
    if (!selectedWebsite) {
      showToastMessage(t('noWebsiteSelected'), 'error');
      return;
    }

    if (!reason) {
      showToastMessage(t('rejectionReasonRequired'), 'error');
      return;
    }

    // Start fade-out animation
    setFadingOutUploads(prev => new Set([...prev, uploadId]));

    try {
      console.log(`🔴 Rejecting upload ${uploadId} for website ${selectedWebsite} with reason: ${reason}`);
      
      const response = await fetch('/.netlify/functions/admin-update-upload', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          uploadId: uploadId,
          status: 2, // Rejected status
          website: selectedWebsite,
          adminEmail: admin?.email || 'admin',
          reason: reason
        })
      }));

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Upload rejected successfully:', data);
        
        showToastMessage(t('uploadRejectedSuccess'), 'success');
        
        // Remove from local state after fade animation (800ms)
        setTimeout(() => {
          setUploads(prevUploads => prevUploads.filter(upload => upload.id !== uploadId));
          setFadingOutUploads(prev => {
            const newSet = new Set(prev);
            newSet.delete(uploadId);
            return newSet;
          });
          setSelectedUploads(prev => {
            const newSet = new Set(prev);
            newSet.delete(uploadId);
            return newSet;
          });
        }, 800);
        
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to reject upload:', errorData);
        showToastMessage(t('errorRejecting').replace('{error}', errorData.error || t('unknownError')), 'error');
        
        // Remove fade-out state on error
        setFadingOutUploads(prev => {
          const newSet = new Set(prev);
          newSet.delete(uploadId);
          return newSet;
        });
      }
    } catch (error) {
      console.error('❌ Network error rejecting upload:', error);
      showToastMessage(t('networkErrorRejecting'), 'error');
      
      // Remove fade-out state on error
      setFadingOutUploads(prev => {
        const newSet = new Set(prev);
        newSet.delete(uploadId);
        return newSet;
      });
    }
  };

  // Show rejection confirmation modal
  const showRejectionConfirmation = (upload) => {
    setUploadToReject(upload);
    setRejectionReason('');
    setShowRejectionModal(true);
  };

  // Handle rejection confirmation
  const confirmRejection = async () => {
    if (!uploadToReject || !rejectionReason) {
      showToastMessage(t('selectRejectionReason'), 'error');
      return;
    }
    
    setShowRejectionModal(false);
    await handleRejectUpload(uploadToReject.id, rejectionReason);
    setUploadToReject(null);
    setRejectionReason('');
  };

  // Show approval confirmation modal
  const showApprovalConfirmation = (upload) => {
    // Check if blur reason is selected for this upload
    if (blurReasonSelected[upload.id]) {
      // Approve with blur directly (no modal needed)
      handleApproveUpload(upload.id, true, blurReasonSelected[upload.id]);
    } else {
      // Normal approval
      setUploadToApprove(upload);
      setShowApprovalModal(true);
    }
  };

  // Handle blur reason selection
  const handleBlurReasonChange = (uploadId, reason) => {
    if (reason === '') {
      // Clear selection
      setBlurReasonSelected(prev => {
        const newState = { ...prev };
        delete newState[uploadId];
        return newState;
      });
    } else {
      setBlurReasonSelected(prev => ({
        ...prev,
        [uploadId]: reason
      }));
    }
  };

  // Image popup functions
  const handleImageClick = useCallback((imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  }, []);

  const closeImageModal = useCallback(() => {
    setShowImageModal(false);
    setSelectedImage(null);
    setSelectedImageBlur(null);
    setRevealBlurredImage(false);
  }, []);

  // Text modal functions
  const handleVisionClick = useCallback((vision) => {
    setSelectedVision(vision);
    setShowVisionModal(true);
  }, []);

  const closeVisionModal = useCallback(() => {
    setShowVisionModal(false);
    setSelectedVision('');
  }, []);

  const handleAboutClick = useCallback((about) => {
    setSelectedAbout(about);
    setShowAboutModal(true);
  }, []);

  const closeAboutModal = useCallback(() => {
    setShowAboutModal(false);
    setSelectedAbout('');
  }, []);

  const needsReadMore = (text, maxLength = 150) => {
    if (!text) return false;
    
    // Check if text contains HTML
    const hasHtml = /<[^>]+>/.test(text);
    
    if (hasHtml) {
      // For HTML content, check the text content length
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      return textContent.length > maxLength;
    }
    
    // For plain text
    return text.length > maxLength;
  };

  // Date formatting utility - DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString; // Return original if error
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (!admin) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="h-screen bg-gray-900 text-white flex overflow-hidden">
      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed bottom-4 right-4 z-[10001] max-w-sm w-full bg-white rounded-lg shadow-lg pointer-events-auto ${
          toastType === 'success' ? 'border-l-4 border-green-400' : 'border-l-4 border-red-400'
        }`}>
          <div className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {toastType === 'success' ? (
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3 w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {toastMessage}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  onClick={() => setShowToast(false)}
                  className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar - Using reusable component */}
      <Sidebar 
        activeSidebarItem={activeSidebarItem}
        isMobileMenuOpen={isMobileMenuOpen}
        onCloseMobileMenu={closeMobileMenu}
        onWebsiteSelect={handleWebsiteSelect}
        selectedWebsite={selectedWebsite}
        dashboardType="admin"
      />

      {/* Mobile Backdrop Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Main Content - Scrollable */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header - Fixed */}
        <div className="bg-gray-800 p-[14px] flex justify-between items-center flex-shrink-0">
          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div>
              <h2 className="text-xl lg:text-2xl font-bold">{t('adminRole')} {t('dashboard')}</h2>
              <p className="text-sm text-gray-400">
                <SecureText>{t('welcome')} {admin.name || admin.email}</SecureText>
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 lg:space-x-4">
            {/* Admin Status Badge */}
            <div className="hidden sm:flex items-center space-x-2 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span>Admin</span>
            </div>

            {/* Search Bar - Hide on very small screens */}
            <div className="relative hidden sm:block">
              <input
                type="text"
                placeholder="Rechercher..."
                className="w-48 lg:w-80 bg-gray-700 text-white placeholder-gray-400 px-4 py-2 pl-10 rounded-lg border border-gray-600 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <i className="fas fa-search text-gray-400"></i>
              </div>
            </div>

            {/* Search Icon for Mobile */}
            <button className="sm:hidden text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors">
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>

        {/* Content Area - Scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto sidebar-scroll" id="main-content-area">
          <div className="p-3 lg:p-6 min-h-full">

            {/* Main Section - Tabbed Interface */}
            <div className="bg-gray-800 rounded-xl min-h-0 flex flex-col">
              {/* Tab Headers */}
              <div className="flex border-b border-gray-700 overflow-x-auto flex-shrink-0">
                <button
                  onClick={() => handleTabChange('overview')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'overview'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('overviewTab')}
                </button>
                <button
                  onClick={() => handleTabChange('users')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'users'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('userManagementTab')}
                </button>
                <button
                  onClick={() => handleTabChange('analytics')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'analytics'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('analyticsTab')}
                </button>
                <button
                  onClick={() => handleTabChange('applications')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'applications'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('applicationsTab')}
                </button>
                <button
                  onClick={() => handleTabChange('suggestions')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'suggestions'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('suggestionsTab')}
                </button>
                <button
                  onClick={() => handleTabChange('top-designs')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'top-designs'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  Top Design
                </button>
                <button
                  onClick={() => handleTabChange('image-manager')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'image-manager'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('imageManagerTab')}
                </button>
                <button
                  onClick={() => handleTabChange('top-votes')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'top-votes'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  Top Voters
                </button>
                <button
                  onClick={() => handleTabChange('draw')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'draw'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  Draw
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-3 lg:p-6 flex-1 min-h-0 overflow-y-auto sidebar-scroll">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6 min-h-0">
                    {selectedWebsite ? (
                      <>
                        {/* Selected Website Section */}
                        <div className="bg-gray-700 rounded-xl p-6">
                          <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-white">
                              Website : <span className="text-cyan-400">{selectedWebsite}</span>
                            </h2>
                            <div className="flex items-center space-x-4">
                              {/* Display Limit Filter */}
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-300">{t('showLabel')}</label>
                                <select
                                  value={displayLimit}
                                  onChange={(e) => handleDisplayLimitChange(parseInt(e.target.value))}
                                  className="bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent cursor-pointer"
                                >
                                  <option value={6}>6</option>
                                  <option value={12}>12</option>
                                  <option value={24}>24</option>
                                  <option value={50}>50</option>
                                </select>
                              </div>
                              
                              {/* Sort Order Filter */}
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-300">Tri:</label>
                                <select
                                  value={sortOrder}
                                  onChange={(e) => handleSortOrderChange(e.target.value)}
                                  className="bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent cursor-pointer"
                                >
                                  <option value="desc">Date décroissante</option>
                                  <option value="asc">Date croissante</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          
                          {/* Upload Count */}
                          <div className="text-gray-300">
                            {loadingUploads ? (
                              <p>{t('loadingUploads')}</p>
                            ) : (
                              <p>{uploads.length > 0 ? t('uploadsFoundPending').replace('{count}', uploads.length) : t('noPendingImages')}</p>
                            )}
                          </div>
                        </div>

                        {/* Uploads Grid */}
                        {loadingUploads ? (
                          <div className="bg-gray-700 rounded-xl p-6">
                            <div className="flex items-center justify-center py-12">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
                            </div>
                          </div>
                        ) : uploads.length > 0 ? (
                          <div className="bg-gray-700 rounded-xl p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                              {uploads.map((upload) => (
                                <div 
                                  key={upload.id} 
                                  className={`bg-gray-600 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-800 ${
                                    fadingOutUploads.has(upload.id) 
                                      ? 'opacity-0 scale-95 transform' 
                                      : 'opacity-100 scale-100'
                                  }`}
                                >
                                  {/* Image */}
                                  <div className="aspect-square bg-gray-500 relative">
                                    {upload.image_url ? (
                                      <img 
                                        src={upload.image_url} 
                                        alt={upload.title}
                                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => {
                                          setSelectedImage(upload.image_url);
                                          setShowImageModal(true);
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        <i className="fas fa-image text-2xl"></i>
                                      </div>
                                    )}
                                    
                                    {/* Status Badge - Replace with Action Buttons */}
                                    <div className="absolute top-2 lg:top-3 left-2 lg:left-3">
                                      <div className="flex items-center space-x-2">
                                        {/* Check Button */}
                                        <button 
                                          onClick={() => showApprovalConfirmation(upload)}
                                          className={`text-white p-2 rounded-lg shadow-lg transition-colors ${
                                            fadingOutUploads.has(upload.id)
                                              ? 'bg-gray-500 cursor-not-allowed'
                                              : 'bg-green-600 hover:bg-green-700'
                                          }`}
                                          title={blurReasonSelected[upload.id] ? t('approveWithBlur') : t('approve')}
                                          disabled={fadingOutUploads.has(upload.id)}
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                          </svg>
                                        </button>
                                        
                                        {/* X Button */}
                                        <button 
                                          onClick={() => showRejectionConfirmation(upload)}
                                          className={`text-white p-2 rounded-lg shadow-lg transition-colors ${
                                            fadingOutUploads.has(upload.id)
                                              ? 'bg-gray-500 cursor-not-allowed'
                                              : 'bg-red-600 hover:bg-red-700'
                                          }`}
                                          title={t('reject')}
                                          disabled={fadingOutUploads.has(upload.id)}
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                        </button>
                                        
                                        {/* Blur Button */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setBlurModalUploadId(upload.id);
                                          }}
                                          className={`p-2 rounded-lg shadow-lg transition-colors ${
                                            fadingOutUploads.has(upload.id)
                                              ? 'bg-gray-500 cursor-not-allowed'
                                              : blurReasonSelected[upload.id]
                                                ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                                : 'bg-gray-600 hover:bg-gray-500 text-white'
                                          }`}
                                          title={blurReasonSelected[upload.id] ? blurReasonSelected[upload.id].substring(0, 50) + '...' : t('blurImage')}
                                          disabled={fadingOutUploads.has(upload.id)}
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            {blurReasonSelected[upload.id] ? (
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            ) : (
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            )}
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                    
                                    {/* Checkbox */}
                                    <div className="absolute top-2 lg:top-3 right-2 lg:right-3">
                                      <input 
                                        type="checkbox" 
                                        className="w-5 h-5 rounded bg-gray-600 border-gray-500 text-cyan-500 focus:ring-cyan-500 focus:ring-2"
                                        checked={selectedUploads.has(upload.id)}
                                        onChange={(e) => {
                                          const newSelected = new Set(selectedUploads);
                                          if (e.target.checked) {
                                            newSelected.add(upload.id);
                                          } else {
                                            newSelected.delete(upload.id);
                                          }
                                          setSelectedUploads(newSelected);
                                          console.log('Selected uploads:', Array.from(newSelected));
                                        }}
                                      />
                                    </div>
                                  </div>
                                  
                                  {/* Content */}
                                  <div className="p-3 lg:p-5 space-y-2 lg:space-y-3">
                                    {/* Title */}
                                    <div>
                                      <SecureText 
                                        className="font-semibold text-white text-base lg:text-lg leading-tight block" 
                                        title={upload.title}
                                      >
                                        {upload.title}
                                      </SecureText>
                                    </div>
                                    
                                    {/* Author Info */}
                                    <div className="flex items-center space-x-2">
                                      <div className="w-6 h-6 lg:w-8 lg:h-8 bg-gray-500 rounded-full flex items-center justify-center">
                                        <i className="fas fa-user text-gray-300 text-xs lg:text-sm"></i>
                                      </div>
                                      <div>
                                        <SecureText className="text-xs lg:text-sm text-gray-300 font-medium block">
                                          {upload.author}
                                        </SecureText>
                                        <SecureText className="text-xs text-gray-400 block">
                                          {upload.email}
                                        </SecureText>
                                        <p className="text-xs text-gray-400">
                                          {formatDate(upload.submittedDate)}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Website Info */}
                                    <div className="bg-gray-500 rounded-lg p-2 lg:p-3">
                                      <div className="flex items-center space-x-2">
                                        <i className="fas fa-globe text-cyan-400 text-xs lg:text-sm"></i>
                                        <SecureText className="text-xs lg:text-sm text-gray-200 font-medium block">
                                          {upload.website}
                                        </SecureText>
                                      </div>
                                    </div>
                                    
                                    {/* Challenge Info - Only show if challenge_id exists and is not 0 */}
                                    {Boolean(upload.challenge_id) && upload.challenge_id !== 0 && (
                                      <div className="bg-gray-600 rounded-lg p-2 lg:p-3 space-y-1 lg:space-y-2">
                                        <div className="flex items-center space-x-2">
                                          <i className="fas fa-trophy text-yellow-500 text-xs lg:text-sm"></i>
                                          <SecureText className="text-xs lg:text-sm text-gray-200 font-medium block">
                                            {upload.challengeTitle}
                                          </SecureText>
                                        </div>
                                        {upload.challengeExpiration && upload.challengeExpiration !== 'N/A' && (
                                          <div className="flex items-center space-x-2">
                                            <i className="fas fa-clock text-orange-400 text-xs lg:text-sm"></i>
                                            <span className="text-xs text-gray-300">
                                              Expire le: {formatDate(upload.challengeExpiration)}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Available Products */}
                                    <AvailableProducts products={upload.products} compact />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-700 rounded-xl p-6">
                            <div className="text-center py-12">
                              <i className="fas fa-inbox text-4xl text-gray-500 mb-4"></i>
                              <h3 className="text-xl font-semibold text-gray-300 mb-2">Aucune image en attente</h3>
                              <p className="text-gray-400">Aucune image en attente pour ce site web.</p>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Welcome Section */}
                        <div className="bg-gray-700 rounded-xl p-6">
                          <div className="text-center">
                            <h2 className="text-2xl font-bold text-white mb-4">
                              {t('welcomeAdminDashboard')}
                            </h2>
                            <p className="text-gray-300 mb-4">
                              {t('connectedAs')} <span className="text-cyan-400 font-semibold">
                                <SecureText>{admin.name || admin.email}</SecureText>
                              </span>
                            </p>
                            <p className="text-gray-300 mb-6">
                              {t('ownerStatus')} <span className="text-green-400 font-semibold">
                                <SecureText>{admin.owner}</SecureText>
                              </span>
                            </p>
                            <div className="bg-blue-600 bg-opacity-20 border border-blue-600 rounded-lg p-4">
                              <div className="flex items-center space-x-2 justify-center">
                                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                <span className="text-blue-400 font-medium">{t('informationLabel')}</span>
                              </div>
                              <p className="text-blue-200 mt-2 text-sm text-center">
                                {t('selectWebsiteInSidebar')}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="bg-gray-700 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-white mb-4">{t('quickActions')}</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <button 
                              onClick={() => handleTabChange('users')}
                              className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg text-left transition-colors"
                            >
                              <div className="flex items-center space-x-3">
                                <i className="fas fa-users text-2xl"></i>
                                <div>
                                  <h4 className="font-semibold">{t('manageUsers')}</h4>
                                  <p className="text-sm opacity-75">{t('viewModifyUserAccounts')}</p>
                                </div>
                              </div>
                            </button>
                            
                            <button 
                              onClick={() => handleTabChange('analytics')}
                              className="bg-green-600 hover:bg-green-700 text-white p-4 rounded-lg text-left transition-colors"
                            >
                              <div className="flex items-center space-x-3">
                                <i className="fas fa-chart-bar text-2xl"></i>
                                <div>
                                  <h4 className="font-semibold">{t('viewAnalytics')}</h4>
                                  <p className="text-sm opacity-75">{t('detailedStatisticsReports')}</p>
                                </div>
                              </div>
                            </button>
                            
                            <button className="bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-lg text-left transition-colors">
                              <div className="flex items-center space-x-3">
                                <i className="fas fa-cog text-2xl"></i>
                                <div>
                                  <h4 className="font-semibold">{t('systemSettings')}</h4>
                                  <p className="text-sm opacity-75">{t('globalConfiguration')}</p>
                                </div>
                              </div>
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Streamer Management Tab */}
                {activeTab === 'users' && (
                  <StreamerManagement t={t} />
                )}

                {/* Analytics Tab */}
                {activeTab === 'analytics' && (
                  <div className="space-y-6">
                    <div className="bg-gray-700 rounded-xl p-6">
                      <h3 className="text-xl font-bold text-white mb-4">{t('analyticsStatisticsTitle')}</h3>
                      <p className="text-gray-300 mb-4">
                        {t('analyticsFullDashboardComingSoon')}
                      </p>
                      <div className="bg-blue-600 bg-opacity-20 border border-blue-600 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                          </svg>
                          <span className="text-blue-400 font-medium">Prochainement</span>
                        </div>
                        <p className="text-blue-200 mt-2 text-sm">
                          Cette section contiendra des analyses détaillées sur l'utilisation, les performances et les métriques clés.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Applications Tab */}
                {activeTab === 'applications' && (
                  <div className="space-y-4">
                    {/* Header & Stats */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-bold text-white mb-1 flex items-center">
                            <div className="w-6 h-6 bg-gradient-to-r from-pink-500 to-purple-600 rounded-md flex items-center justify-center mr-2">
                              <i className="fas fa-user-plus text-white text-xs"></i>
                            </div>
                            Application Management
                          </h3>
                          <div className="text-gray-400">
                            {loadingApplications ? (
                              <div className="flex items-center">
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-600 border-t-pink-500 mr-2"></div>
                                <span className="text-xs">Loading applications...</span>
                              </div>
                            ) : (
                              <span className="text-xs">
                                <span className="text-white font-semibold">{applications.length}</span> applications found
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Status Cards */}
                        <div className="flex gap-2">
                          <div className="bg-gray-700 rounded-lg p-3 min-w-[65px] text-center border border-gray-600 hover:border-yellow-500 transition-all duration-200">
                            <div className="text-yellow-400 font-bold text-xl mb-0.5">
                              {applications.filter(app => app.status === 'pending').length}
                            </div>
                            <div className="text-gray-300 text-xs font-medium">Pending</div>
                          </div>
                          <div className="bg-gray-700 rounded-lg p-3 min-w-[65px] text-center border border-gray-600 hover:border-green-500 transition-all duration-200">
                            <div className="text-green-400 font-bold text-xl mb-0.5">
                              {applications.filter(app => app.status === 'approved').length}
                            </div>
                            <div className="text-gray-300 text-xs font-medium">Approved</div>
                          </div>
                          <div className="bg-gray-700 rounded-lg p-3 min-w-[65px] text-center border border-gray-600 hover:border-red-500 transition-all duration-200">
                            <div className="text-red-400 font-bold text-xl mb-0.5">
                              {applications.filter(app => app.status === 'rejected').length}
                            </div>
                            <div className="text-gray-300 text-xs font-medium">Rejected</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-base font-semibold text-white flex items-center">
                          <div className="w-5 h-5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-md flex items-center justify-center mr-2">
                            <i className="fas fa-filter text-white text-xs"></i>
                          </div>
                          Search & Filter
                        </h4>
                        <button
                          onClick={() => {
                            setApplicationsFilter('all');
                            setEmailSearch('');
                            setSocialFilter('');
                            setSubscribersMin('');
                            setSubscribersMax('');
                          }}
                          className="bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1.5 border border-gray-600"
                        >
                          <i className="fas fa-times text-xs"></i>
                          Clear All
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Status Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-2">Status</label>
                          <select
                            value={applicationsFilter}
                            onChange={(e) => setApplicationsFilter(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 hover:border-gray-500"
                          >
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </div>

                        {/* Email Search */}
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-2">Search Email</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={emailSearch}
                              onChange={(e) => setEmailSearch(e.target.value)}
                              placeholder="Enter email address..."
                              className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 pl-9 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200 hover:border-gray-500"
                            />
                            <i className="fas fa-envelope absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"></i>
                          </div>
                        </div>

                        {/* Social Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-2">Social Platform</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={socialFilter}
                              onChange={(e) => setSocialFilter(e.target.value)}
                              placeholder="Instagram, TikTok..."
                              className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 pl-9 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 hover:border-gray-500"
                            />
                            <i className="fas fa-share-alt absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"></i>
                          </div>
                        </div>
                      </div>

                      {/* Subscribers Range */}
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <label className="block text-xs font-medium text-gray-300 mb-2">Subscriber Range</label>
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="relative">
                              <input
                                type="number"
                                value={subscribersMin}
                                onChange={(e) => setSubscribersMin(e.target.value)}
                                placeholder="Min subscribers"
                                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 pl-9 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-500"
                              />
                              <i className="fas fa-users absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"></i>
                            </div>
                          </div>
                          <div className="text-gray-400 font-medium text-xs px-1">to</div>
                          <div className="flex-1">
                            <div className="relative">
                              <input
                                type="number"
                                value={subscribersMax}
                                onChange={(e) => setSubscribersMax(e.target.value)}
                                placeholder="Max subscribers"
                                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 pl-9 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-500"
                              />
                              <i className="fas fa-users absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Applications List */}
                    {loadingApplications ? (
                      <div className="bg-gray-800 rounded-xl p-8 border border-gray-600">
                        <div className="flex flex-col items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-600 border-t-pink-500 mb-4"></div>
                          <p className="text-gray-300 text-lg">Loading applications...</p>
                        </div>
                      </div>
                    ) : applications.length > 0 ? (
                      <div className="space-y-4">
                                                {applications.map((application) => {
                          const { flagClass } = getFlagDisplay(application.country);
                          
                          return (
                            <div key={application.id} className="bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden">
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
                                {/* Column 1 - Main Info */}
                                <div className="lg:col-span-1">
                                  {/* Action Buttons */}
                                  {application.status === 'pending' && (
                                    <div className="flex items-center justify-center gap-3 mb-6">
                                      <button 
                                        onClick={() => handleApproveApplication(application.id)}
                                        disabled={processingApplication === application.id}
                                        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-500 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
                                      >
                                        {processingApplication === application.id ? (
                                          <i className="fas fa-spinner fa-spin text-sm"></i>
                                        ) : (
                                          <i className="fas fa-check text-sm"></i>
                                        )}
                                        Approve
                                      </button>
                                      <button 
                                        onClick={() => handleDismissApplication(application.id)}
                                        disabled={processingApplication === application.id}
                                        className="bg-red-600 hover:bg-red-700 disabled:bg-gray-500 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
                                      >
                                        {processingApplication === application.id ? (
                                          <i className="fas fa-spinner fa-spin text-sm"></i>
                                        ) : (
                                          <i className="fas fa-times text-sm"></i>
                                        )}
                                        Reject
                                      </button>
                                    </div>
                                  )}

                                  <div className="flex items-start gap-4">
                                    {/* Avatar */}
                                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
                                      <SecureText>{application.name?.charAt(0)?.toUpperCase() || 'U'}</SecureText>
                                    </div>
                                    
                                    {/* Main Details */}
                                    <div className="flex-1">
                                      <div className="flex items-center gap-3 mb-3">
                                        <h4 className="text-lg font-bold text-white">
                                          <SecureText>{application.name}</SecureText>
                                        </h4>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium uppercase tracking-wide ${
                                          application.status === 'pending' 
                                            ? 'bg-yellow-500 bg-opacity-20 text-yellow-400 border border-yellow-500'
                                            : application.status === 'approved'
                                            ? 'bg-green-500 bg-opacity-20 text-green-400 border border-green-500'
                                            : application.status === 'rejected'
                                            ? 'bg-red-500 bg-opacity-20 text-red-400 border border-red-500'
                                            : 'bg-gray-500 bg-opacity-20 text-gray-400 border border-gray-500'
                                        }`}>
                                          {application.status === 'pending' ? 'On Hold' :
                                           application.status === 'approved' ? 'Approved' :
                                           application.status === 'rejected' ? 'Rejected' :
                                           application.status}
                                        </span>
                                      </div>
                                      
                                      <div className="space-y-2 text-sm text-gray-300 mb-4">
                                        <div className="flex items-center gap-2">
                                          <i className="fas fa-envelope text-cyan-400 w-4"></i>
                                          <SecureText>{application.email}</SecureText>
                                        </div>
                                        
                                        {/* Social & Subscribers Info */}
                                        {(application.social || application.subscriber) && (
                                          <div className="flex flex-col gap-2 my-3">
                                            {application.social && (
                                              <div className="bg-purple-600 bg-opacity-20 px-3 py-2 rounded-md border border-purple-500">
                                                <div className="flex items-center gap-2 mb-2">
                                                  <i className="fas fa-share-alt text-purple-400"></i>
                                                  <span className="text-purple-300 font-medium text-sm">Social Media</span>
                                                </div>
                                                <div className="flex flex-wrap gap-1">
                                                  {(() => {
                                                    try {
                                                      const socialUrls = JSON.parse(application.social);
                                                      if (Array.isArray(socialUrls)) {
                                                        return socialUrls.map((url, index) => {
                                                          // Determine platform and icon
                                                          let platform = 'link';
                                                          let icon = 'fas fa-link';
                                                          let bgColor = 'bg-gray-600';
                                                          let textColor = 'text-gray-200';
                                                          
                                                          if (url.includes('youtube.com')) {
                                                            platform = 'YouTube';
                                                            icon = 'fab fa-youtube';
                                                            bgColor = 'bg-red-600';
                                                            textColor = 'text-white';
                                                          } else if (url.includes('tiktok.com')) {
                                                            platform = 'TikTok';
                                                            icon = 'fab fa-tiktok';
                                                            bgColor = 'bg-black';
                                                            textColor = 'text-white';
                                                          } else if (url.includes('instagram.com')) {
                                                            platform = 'Instagram';
                                                            icon = 'fab fa-instagram';
                                                            bgColor = 'bg-pink-600';
                                                            textColor = 'text-white';
                                                          } else if (url.includes('twitter.com') || url.includes('x.com')) {
                                                            platform = 'Twitter/X';
                                                            icon = 'fab fa-twitter';
                                                            bgColor = 'bg-blue-500';
                                                            textColor = 'text-white';
                                                          } else if (url.includes('twitch.tv')) {
                                                            platform = 'Twitch';
                                                            icon = 'fab fa-twitch';
                                                            bgColor = 'bg-purple-600';
                                                            textColor = 'text-white';
                                                          } else if (url.includes('facebook.com')) {
                                                            platform = 'Facebook';
                                                            icon = 'fab fa-facebook';
                                                            bgColor = 'bg-blue-700';
                                                            textColor = 'text-white';
                                                          } else if (url.includes('discord.gg')) {
                                                            platform = 'Discord';
                                                            icon = 'fab fa-discord';
                                                            bgColor = 'bg-indigo-600';
                                                            textColor = 'text-white';
                                                          } else if (url.includes('snapchat.com')) {
                                                            platform = 'Snapchat';
                                                            icon = 'fab fa-snapchat';
                                                            bgColor = 'bg-yellow-400';
                                                            textColor = 'text-black';
                                                          } else if (url.includes('linkedin.com')) {
                                                            platform = 'LinkedIn';
                                                            icon = 'fab fa-linkedin';
                                                            bgColor = 'bg-blue-600';
                                                            textColor = 'text-white';
                                                          }
                                                          
                                                          return (
                                                            <a
                                                              key={index}
                                                              href={url}
                                                              target="_blank"
                                                              rel="noopener noreferrer"
                                                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 hover:scale-105 hover:shadow-md ${bgColor} ${textColor}`}
                                                              title={url}
                                                            >
                                                              <i className={`${icon} text-xs`}></i>
                                                              <span>{platform}</span>
                                                            </a>
                                                          );
                                                        });
                                                      } else {
                                                        // Fallback for non-array format
                                                        return (
                                                          <span className="text-purple-300 text-xs">
                                                            <SecureText>{application.social}</SecureText>
                                                          </span>
                                                        );
                                                      }
                                                    } catch (error) {
                                                      // Fallback for invalid JSON
                                                      return (
                                                        <span className="text-purple-300 text-xs">
                                                          <SecureText>{application.social}</SecureText>
                                                        </span>
                                                      );
                                                    }
                                                  })()}
                                                </div>
                                              </div>
                                            )}
                                            {application.subscriber && (
                                              <div className="flex items-center gap-2 bg-blue-600 bg-opacity-20 px-3 py-2 rounded-md border border-blue-500">
                                                <i className="fas fa-users text-blue-400"></i>
                                                <span className="text-blue-300 font-medium text-sm">
                                                  {application.subscriber.toLocaleString()} subscribers
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        
                                        <div className="flex items-center gap-2">
                                          {flagClass && <span className={`${flagClass} w-4 h-3 rounded-sm shadow-sm`}></span>}
                                          <i className="fas fa-globe text-blue-400 w-4"></i>
                                          <SecureText>{application.country}</SecureText>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <i className="fas fa-calendar text-green-400 w-4"></i>
                                          <span>{formatDate(application.submittedDate)}</span>
                                        </div>
                                        {application.ip_address && (
                                          <div className="flex items-center gap-2">
                                            <i className="fas fa-network-wired text-purple-400 w-4"></i>
                                            <span className="font-mono text-xs">{application.ip_address}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Column 2 - Vision & About */}
                                <div className="lg:col-span-1">
                                  <div className="h-full space-y-4">
                                    {/* Streamer Link */}
                                    {application.streamer && (
                                      <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                                        <i className="fas fa-user-circle text-pink-400 w-4"></i>
                                        <span className="text-gray-300">Streamer:</span>
                                        <a 
                                          href={`https://preproductiondev.netlify.app/${encodeURIComponent(application.streamer)}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-pink-400 hover:text-pink-300 transition-colors duration-200 hover:underline"
                                        >
                                          <SecureText>{application.streamer}</SecureText>
                                        </a>
                                      </div>
                                    )}
                                    
                                    {/* Vision */}
                                    <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4 border border-gray-600">
                                      <h5 className="text-sm font-semibold text-gray-300 mb-2 flex items-center">
                                        <i className="fas fa-eye text-cyan-400 mr-2"></i>
                                        Vision:
                                      </h5>
                                      <div className="text-gray-200 text-sm leading-relaxed">
                                        <MultilingualText text={application.message} fieldType="message" truncate={true} maxLength={150} />
                                        {needsReadMore(application.message) && (
                                          <button
                                            onClick={() => handleVisionClick(application.message)}
                                            className="text-cyan-400 hover:text-cyan-300 text-xs font-medium ml-2 underline transition-colors"
                                          >
                                            Read more
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* About */}
                                    {application.about && (
                                      <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4 border border-gray-600">
                                        <h5 className="text-sm font-semibold text-gray-300 mb-2 flex items-center">
                                          <i className="fas fa-user text-purple-400 mr-2"></i>
                                          About:
                                        </h5>
                                        <div className="text-gray-200 text-sm leading-relaxed">
                                          <MultilingualText text={application.about} fieldType="about" truncate={true} maxLength={150} />
                                          {needsReadMore(application.about) && (
                                            <button
                                              onClick={() => handleAboutClick(application.about)}
                                              className="text-purple-400 hover:text-purple-300 text-xs font-medium ml-2 underline transition-colors"
                                            >
                                              Read more
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Column 3 - Image Preview */}
                                <div className="lg:col-span-1">
                                  <div className="h-full">
                                    {/* Image Preview */}
                                    {application.image && (
                                      <div className="bg-blue-600 bg-opacity-20 rounded-lg border border-blue-500 overflow-hidden relative">
                                        <div className="flex items-center justify-between p-3 bg-blue-600 bg-opacity-30 border-b border-blue-500">
                                          <div className="flex items-center gap-3">
                                            <i className="fas fa-images text-blue-400"></i>
                                            <span className="text-blue-300 font-medium text-sm">Application Images</span>
                                          </div>
                                          
                                          {/* View Mode Dropdown */}
                                          <div className="relative">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenDropdown(openDropdown === application.id ? null : application.id);
                                              }}
                                              className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-3 py-1.5 rounded flex items-center gap-2 transition-colors"
                                            >
                                              <span className="capitalize">{imageViewMode[application.id] || 'profile'}</span>
                                              <i className="fas fa-chevron-down text-xs"></i>
                                            </button>
                                            
                                            {/* Dropdown Menu */}
                                            {openDropdown === application.id && (
                                              <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-600 rounded shadow-xl z-50 min-w-[120px]">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setImageViewMode(prev => ({ ...prev, [application.id]: 'profile' }));
                                                    setCurrentImageIndex(prev => ({ ...prev, [application.id]: 0 }));
                                                    setOpenDropdown(null);
                                                  }}
                                                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                >
                                                  <i className="fas fa-user text-blue-400"></i>
                                                  Profile
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setImageViewMode(prev => ({ ...prev, [application.id]: 'logo' }));
                                                    setCurrentImageIndex(prev => ({ ...prev, [application.id]: 0 }));
                                                    setOpenDropdown(null);
                                                  }}
                                                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                >
                                                  <i className="fas fa-paint-brush text-green-400"></i>
                                                  Logo
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {/* Stacked Card Layout with Navigation */}
                                        <div className="p-4 bg-gray-900 relative">
                                          {(() => {
                                            let imageUrls = [];
                                            const viewMode = imageViewMode[application.id] || 'profile';
                                            
                                            try {
                                              // Check if it's the new object format with profile and logo
                                              if (application.image.startsWith('{')) {
                                                const imageData = JSON.parse(application.image);
                                                
                                                // Filter images based on view mode
                                                if (viewMode === 'profile') {
                                                  // Show profile images
                                                  if (imageData.profile && Array.isArray(imageData.profile)) {
                                                    imageUrls = imageData.profile;
                                                  }
                                                } else if (viewMode === 'logo') {
                                                  // Show logo images
                                                  if (imageData.logo && Array.isArray(imageData.logo)) {
                                                    imageUrls = imageData.logo;
                                                  }
                                                }
                                              }
                                              // Handle old JSON array format
                                              else if (application.image.startsWith('[')) {
                                                imageUrls = JSON.parse(application.image);
                                              } 
                                              // Handle legacy single image format
                                              else {
                                                imageUrls = [application.image];
                                              }
                                            } catch (error) {
                                              console.error('Error parsing images:', error);
                                              // Fallback to single image if JSON parsing fails
                                              imageUrls = [application.image];
                                            }
                                            
                                            // Get current index for this application (default to 0)
                                            const currentIndex = currentImageIndex[application.id] || 0;
                                            
                                            // Navigation functions
                                            const goToPrevious = () => {
                                              setCurrentImageIndex(prev => ({
                                                ...prev,
                                                [application.id]: currentIndex > 0 ? currentIndex - 1 : imageUrls.length - 1
                                              }));
                                            };
                                            
                                            const goToNext = () => {
                                              setCurrentImageIndex(prev => ({
                                                ...prev,
                                                [application.id]: currentIndex < imageUrls.length - 1 ? currentIndex + 1 : 0
                                              }));
                                            };
                                            
                                            return (
                                              <div key={`${application.id}-${viewMode}`} className="relative h-80 flex items-center justify-center">
                                                {/* Stacked Images */}
                                                {imageUrls.map((imageUrl, index) => {
                                                  // Calculate display order based on current index
                                                  const displayIndex = (index - currentIndex + imageUrls.length) % imageUrls.length;
                                                  
                                                  return (
                                                    <div
                                                      key={`${viewMode}-${index}`}
                                                      className="absolute w-52 h-64 bg-gray-800 rounded-lg shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105"
                                                      style={{
                                                        zIndex: imageUrls.length - displayIndex,
                                                        transform: `translateX(${displayIndex * 18}px) translateY(${displayIndex * 12}px)`,
                                                        opacity: displayIndex > 3 ? 0 : 1, // Hide images beyond 4th position
                                                      }}
                                                      onClick={() => handleImageClick(imageUrl)}
                                                    >
                                                      {/* Image container */}
                                                      <div className="relative w-full h-full p-3">
                                                        <div className="relative w-full h-full bg-gray-700 rounded-md overflow-hidden">
                                                          <img
                                                            src={imageUrl}
                                                            alt={`${viewMode} ${index + 1}`}
                                                            className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                                                            onError={(e) => {
                                                              e.target.style.display = 'none';
                                                              e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                          />
                                                          <div className="hidden absolute inset-0 bg-gray-300 flex items-center justify-center">
                                                            <div className="text-center">
                                                              <i className="fas fa-exclamation-triangle text-gray-600 text-lg mb-1"></i>
                                                              <p className="text-gray-600 text-xs">Failed to load</p>
                                                            </div>
                                                          </div>
                                                          
                                                          {/* Image number badge */}
                                                          <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full">
                                                            {index + 1}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                                
                                                {/* Navigation buttons - only show if more than 1 image */}
                                                {imageUrls.length > 1 && (
                                                  <>
                                                    {/* Previous button */}
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        goToPrevious();
                                                      }}
                                                      className="absolute left-2 top-1/2 transform -translate-y-1/2 z-50 bg-black bg-opacity-50 hover:bg-opacity-70 text-white w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                                      title="Previous image"
                                                    >
                                                      <i className="fas fa-chevron-left text-sm"></i>
                                                    </button>
                                                    
                                                    {/* Next button */}
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        goToNext();
                                                      }}
                                                      className="absolute right-2 top-1/2 transform -translate-y-1/2 z-50 bg-black bg-opacity-50 hover:bg-opacity-70 text-white w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                                      title="Next image"
                                                    >
                                                      <i className="fas fa-chevron-right text-sm"></i>
                                                    </button>
                                                    
                                                    {/* Image counter */}
                                                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-50 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full">
                                                      {currentIndex + 1} / {imageUrls.length}
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-gray-800 rounded-xl p-8 border border-gray-600">
                        <div className="text-center py-12">
                          <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-user-plus text-3xl text-gray-500"></i>
                          </div>
                          <h3 className="text-2xl font-bold text-gray-300 mb-2">No Applications Found</h3>
                          <p className="text-gray-400 text-lg mb-4">
                            {applicationsFilter === 'all' ? 'No applications have been submitted yet.' :
                             applicationsFilter === 'pending' ? 'No pending applications.' :
                             applicationsFilter === 'approved' ? 'No approved applications.' :
                             'No rejected applications.'}
                          </p>
                          {(emailSearch || socialFilter || subscribersMin || subscribersMax) && (
                            <button
                              onClick={() => {
                                setApplicationsFilter('all');
                                setEmailSearch('');
                                setSocialFilter('');
                                setSubscribersMin('');
                                setSubscribersMax('');
                              }}
                              className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                            >
                              Clear Filters
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Suggestions Tab */}
                {activeTab === 'suggestions' && (
                  <div className="space-y-4">
                    {/* Header & Stats */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-bold text-white mb-1 flex items-center">
                            <div className="w-6 h-6 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-md flex items-center justify-center mr-2">
                              <i className="fas fa-lightbulb text-white text-xs"></i>
                            </div>
                            {t('suggestionsManagementTitle')}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            {t('streamerSuggestionsSubmittedByCommunity')}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-cyan-400">
                              {suggestionsPagination?.total || 0}
                            </div>
                            <div className="text-xs text-gray-400">{t('total')}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Suggestions List */}
                    <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700">
                      {loadingSuggestions ? (
                        <div className="flex justify-center items-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-500 border-t-transparent"></div>
                          <span className="ml-3 text-gray-400">{t('loadingSuggestions')}</span>
                        </div>
                      ) : suggestions.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-lightbulb text-gray-400 text-2xl"></i>
                          </div>
                          <h4 className="text-lg font-medium text-white mb-2">Aucune suggestion</h4>
                          <p className="text-gray-400">Aucune suggestion de streamer n'a été soumise pour le moment.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-700">
                          {suggestions.map((suggestion, index) => (
                            <div key={suggestion.id} className="p-4 hover:bg-gray-750 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                                      <span className="text-white text-sm font-bold">
                                        {suggestion.name.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <div>
                                      <h4 className="text-white font-medium">
                                        <SecureText>{suggestion.name}</SecureText>
                                      </h4>
                                    </div>
                                  </div>
                                  
                                  {suggestion.suggestto && suggestion.suggestto.length > 0 && (
                                    <div className="mt-2 text-xs text-gray-500">
                                      Métadonnées: {JSON.stringify(suggestion.suggestto)}
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex items-center space-x-2 ml-4">
                                  <span className="px-2 py-1 bg-cyan-900 text-cyan-300 text-xs rounded-full">
                                    #{suggestion.id}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {formatDate(suggestion.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Pagination */}
                      {suggestionsPagination && suggestionsPagination.totalPages > 1 && (
                        <div className="p-4 border-t border-gray-700 bg-gray-750">
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-400">
                              Page {suggestionsPagination.page} sur {suggestionsPagination.totalPages}
                              ({suggestionsPagination.total} suggestions au total)
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setSuggestionsPage(suggestionsPagination.page - 1)}
                                disabled={!suggestionsPagination.hasPrev}
                                className={`px-3 py-1 rounded text-sm ${
                                  suggestionsPagination.hasPrev
                                    ? 'bg-gray-600 text-white hover:bg-gray-500'
                                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                }`}
                              >
                                Précédent
                              </button>
                              <span className="text-sm text-gray-400">
                                {suggestionsPagination.page} / {suggestionsPagination.totalPages}
                              </span>
                              <button
                                onClick={() => setSuggestionsPage(suggestionsPagination.page + 1)}
                                disabled={!suggestionsPagination.hasNext}
                                className={`px-3 py-1 rounded text-sm ${
                                  suggestionsPagination.hasNext
                                    ? 'bg-gray-600 text-white hover:bg-gray-500'
                                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                }`}
                              >
                                Suivant
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Top Design Tab */}
                {activeTab === 'top-designs' && (
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-bold text-white mb-1 flex items-center">
                            <div className="w-6 h-6 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-md flex items-center justify-center mr-2">
                              <i className="fas fa-trophy text-white text-xs"></i>
                            </div>
                            Top Design
                          </h3>
                          <p className="text-gray-400 text-sm">Top performing designs by votes and reactions</p>
                        </div>
                        <button
                          onClick={() => fetchTopDesigns()}
                          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <i className="fas fa-sync-alt"></i>
                          <span>Refresh</span>
                        </button>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Website Filter */}
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">
                            <i className="fas fa-globe mr-2"></i>
                            Streamer
                          </label>
                          <select
                            value={topDesignsWebsiteFilter}
                            onChange={(e) => setTopDesignsWebsiteFilter(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="all">All Streamers</option>
                            {availableWebsites.map(website => (
                              <option key={website} value={website}>
                                {website.replace('.com', '')}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Discord Filter */}
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">
                            <i className="fab fa-discord mr-2"></i>
                            Discord Status
                          </label>
                          <select
                            value={topDesignsDiscordFilter}
                            onChange={(e) => setTopDesignsDiscordFilter(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="all">All</option>
                            <option value="with-discord">With Discord</option>
                            <option value="without-discord">Without Discord</option>
                          </select>
                        </div>
                      </div>

                      {/* Clear Filters */}
                      {(topDesignsWebsiteFilter !== 'all' || topDesignsDiscordFilter !== 'all') && (
                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={() => {
                              setTopDesignsWebsiteFilter('all');
                              setTopDesignsDiscordFilter('all');
                            }}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors flex items-center space-x-2 text-sm"
                          >
                            <i className="fas fa-times"></i>
                            <span>Clear Filters</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Loading State */}
                    {loadingTopDesigns ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500"></div>
                      </div>
                    ) : (() => {
                      // Apply filters
                      const filteredDesigns = topDesigns.filter(design => {
                        // Website filter
                        if (topDesignsWebsiteFilter !== 'all' && design.website !== topDesignsWebsiteFilter) {
                          return false;
                        }
                        
                        // Discord filter
                        if (topDesignsDiscordFilter === 'with-discord' && !design.discord_name) {
                          return false;
                        }
                        if (topDesignsDiscordFilter === 'without-discord' && design.discord_name) {
                          return false;
                        }
                        
                        return true;
                      });

                      return filteredDesigns.length === 0 ? (
                        <div className="bg-gray-800 rounded-xl p-8 text-center">
                          <i className="fas fa-image text-gray-600 text-4xl mb-4"></i>
                          <p className="text-gray-400">No designs found matching the filters</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-4">
                          {filteredDesigns.map((design, index) => (
                          <div key={design.id} className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700 hover:border-gray-600 transition-colors">
                            <div className="flex flex-col lg:flex-row gap-4">
                              {/* Rank Badge */}
                              <div className="flex-shrink-0 flex items-center justify-center lg:justify-start">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                                  index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white' :
                                  index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-white' :
                                  index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white' :
                                  'bg-gray-700 text-gray-300'
                                }`}>
                                  #{index + 1}
                                </div>
                              </div>

                              {/* Design Image */}
                              <div className="flex-shrink-0">
                                <img
                                  src={design.image_url}
                                  alt={design.title}
                                  className="w-full lg:w-32 h-32 object-cover rounded-lg"
                                />
                              </div>

                              {/* Design Info */}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-white font-semibold text-lg mb-2 truncate">{design.title}</h4>
                                <div className="space-y-1 text-sm">
                                  <div className="flex items-center text-gray-300">
                                    <i className="fas fa-user w-5 text-cyan-400"></i>
                                    <span className="ml-2">{design.username || 'Unknown'}</span>
                                  </div>
                                  <div className="flex items-center text-gray-300">
                                    <i className="fas fa-envelope w-5 text-purple-400"></i>
                                    <span className="ml-2 truncate">{design.email}</span>
                                  </div>
                                  {design.discord_name && (
                                    <div className="flex items-center text-gray-300">
                                      <i className="fab fa-discord w-5 text-indigo-400"></i>
                                      <span className="ml-2">{design.discord_name}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="mt-2">
                                  <AvailableProducts products={design.products} compact />
                                </div>
                              </div>

                              {/* Stats */}
                              <div className="flex-shrink-0 flex flex-row lg:flex-col gap-3 justify-center lg:justify-start">
                                <div className="bg-gray-700 rounded-lg px-4 py-2 text-center min-w-[100px]">
                                  <div className="flex items-center justify-center space-x-2 mb-1">
                                    <i className="fas fa-heart text-pink-500"></i>
                                    <span className="text-white font-bold text-lg">{design.vote || 0}</span>
                                  </div>
                                  <p className="text-gray-400 text-xs">Votes</p>
                                </div>
                                <div className="bg-gray-700 rounded-lg px-4 py-2 text-center min-w-[100px]">
                                  <div className="flex items-center justify-center space-x-2 mb-1">
                                    <i className="fas fa-smile text-yellow-500"></i>
                                    <span className="text-white font-bold text-lg">
                                      {design.reaction_count || 0}
                                    </span>
                                  </div>
                                  <p className="text-gray-400 text-xs">Reactions</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      );
                    })()}
                  </div>
                )}

                {/* Image Manager Tab */}
                {activeTab === 'image-manager' && (
                  <div className="space-y-4">
                    {/* Header & Stats */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-bold text-white mb-1 flex items-center">
                            <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-600 rounded-md flex items-center justify-center mr-2">
                              <i className="fas fa-images text-white text-xs"></i>
                            </div>
                            {t('imageManagerTab')}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            {t('manageAllImages')}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-400">
                              {allImages.length}
                            </div>
                            <div className="text-xs text-gray-400">{t('totalImages')}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="flex flex-col lg:flex-row gap-4">

                        {/* Website Filter */}
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-300 mb-2">{t('filterByWebsite')}</label>
                          <select
                            value={imageWebsiteFilter}
                            onChange={(e) => setImageWebsiteFilter(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">{t('allWebsites')}</option>
                            {availableWebsites.map((website) => (
                              <option key={website} value={website}>
                                {website}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Status Filter */}
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-300 mb-2">{t('statusLabel')}</label>
                          <select
                            value={imageStatusFilter}
                            onChange={(e) => setImageStatusFilter(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="all">{t('all')}</option>
                            <option value="0">{t('pendingStatus')}</option>
                            <option value="1">{t('approvedStatus')}</option>
                            <option value="2">{t('rejectedStatus')}</option>
                            <option value="9">{t('pendingAKS')}</option>
                          </select>
                        </div>

                       

                        {/* Clear Filter Button */}
                        {(imageStatusFilter !== 'all' || imageWebsiteFilter) && (
                          <div className="flex items-end">
                            <button
                              onClick={() => {
                                setImageStatusFilter('all');
                                setImageWebsiteFilter('');
                              }}
                              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
                            >
                              {t('clearFiltersButton')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Images Grid */}
                    <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-4">
                      {loadingImages ? (
                        <div className="flex justify-center items-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
                          <span className="ml-3 text-gray-400">{t('loadingImages')}</span>
                        </div>
                      ) : allImages.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-images text-gray-400 text-2xl"></i>
                          </div>
                          <h4 className="text-lg font-medium text-white mb-2">{t('noImagesFound')}</h4>
                          <p className="text-gray-400">{t('noImagesMatchCriteria')}</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {allImages.map((image) => {
                            // Determine status badge color and text
                            const getStatusInfo = (status) => {
                              const statusNum = parseInt(status);
                              switch (statusNum) {
                                case 0:
                                  return { color: 'bg-yellow-600 text-yellow-100', text: t('pendingStatus') };
                                case 1:
                                  return { color: 'bg-green-600 text-green-100', text: t('approvedStatus') };
                                case 2:
                                  return { color: 'bg-red-600 text-red-100', text: t('rejectedStatus') };
                                case 9:
                                  return { color: 'bg-blue-600 text-blue-100', text: t('pendingAKS') };
                                default:
                                  return { color: 'bg-gray-600 text-gray-100', text: t('unknown') };
                              }
                            };

                            const statusInfo = getStatusInfo(image.status);

                            return (
                              <div
                                key={image.id}
                                className="bg-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                              >
                                {/* Image */}
                                <div className="aspect-square bg-gray-600 relative group overflow-hidden rounded-t-xl">
                                  {image.image_url ? (
                                    <img
                                      src={image.image_url}
                                      alt={image.title}
                                      className={`w-full h-full object-cover cursor-pointer ${image.blur ? 'blur-xl' : ''}`}
                                      onClick={() => {
                                        setSelectedImage(image.image_url);
                                        setSelectedImageBlur(image.blur || null);
                                        setRevealBlurredImage(false);
                                        setShowImageModal(true);
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                      <i className="fas fa-image text-2xl"></i>
                                    </div>
                                  )}
                                  
                                  {/* Status Badge */}
                                  <div className="absolute top-[20px] right-2">
                                    <span className={`p-[16px] rounded-lg text-xs font-medium shadow-lg ${statusInfo.color}`}>
                                      {statusInfo.text}
                                    </span>
                                  </div>

                                  {/* Website Badge with Trash Icon */}
                                  <div className="absolute top-2 left-2 flex items-center gap-1">
                                    {/* Trash Icon Button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteImageClick(image);
                                      }}
                                      className="bg-red-600 hover:bg-red-700 text-white p-[15px] rounded-lg shadow-lg transition-colors"
                                      title="Delete image"
                                    >
                                      <i className="fas fa-trash text-md"></i>
                                    </button>
                                    
                                    {/* Website Name Badge */}
                                    <span className="p-[14px] rounded-lg text-md font-medium shadow-lg bg-purple-600 text-purple-100">
                                      {image.website ? image.website.replace('.com', '') : 'Unknown'}
                                    </span>
                                  </div>
                                </div>

                                {/* Image Details */}
                                <div className="p-3">
                                  <h4 className="text-white font-medium text-sm truncate mb-1">
                                    <SecureText>{image.title || t('untitled')}</SecureText>
                                  </h4>
                                  <p className="text-gray-400 text-xs truncate">
                                    {t('by')}: <SecureText>{image.first_name} {image.last_name}</SecureText>
                                  </p>
                                  <p className="text-gray-400 text-xs truncate">
                                    Email: <SecureText>{image.email}</SecureText>
                                  </p>
                                  <p className="text-gray-400 text-xs mt-1">
                                    {new Date(image.submitted_at).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </p>
                                  
                                  {/* Votes if available */}
                                  {image.vote !== undefined && (
                                    <div className="mt-2 flex items-center">
                                      <i className="fas fa-heart text-pink-500 text-xs mr-1"></i>
                                      <span className="text-gray-300 text-xs font-medium">{image.vote} {t('votes')}</span>
                                    </div>
                                  )}
                                  
                                  {/* Blur reason if image is blurred */}
                                  {image.blur && (
                                    <div className="mt-2 p-2 bg-red-900/30 rounded-lg border border-red-700">
                                      <p className="text-red-400 text-xs leading-relaxed">
                                        <i className="fas fa-eye-slash mr-1"></i>
                                        {image.blur}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {/* Available Products */}
                                  <div className="mt-2">
                                    <AvailableProducts products={image.products} compact />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Top Votes Tab */}
                {activeTab === 'top-votes' && (
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                          <h2 className="text-2xl font-bold text-white mb-2">Top Voters</h2>
                          <p className="text-gray-400 text-sm">View top voters sorted by total vote count</p>
                        </div>
                        <button
                          onClick={fetchTopVotes}
                          className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <i className="fas fa-sync-alt"></i>
                          <span>Refresh</span>
                        </button>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Streamer Filter */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Streamer
                          </label>
                          <select
                            value={topVotesWebsiteFilter}
                            onChange={(e) => setTopVotesWebsiteFilter(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="all">All Streamers</option>
                            {availableWebsites.map(website => (
                              <option key={website} value={website}>
                                {website.replace('.com', '')}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* From Date Filter */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            From Date
                          </label>
                          <input
                            type="datetime-local"
                            value={topVotesFromDate}
                            onChange={(e) => setTopVotesFromDate(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                        </div>

                        {/* To Date Filter */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            To Date
                          </label>
                          <input
                            type="datetime-local"
                            value={topVotesToDate}
                            onChange={(e) => setTopVotesToDate(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                        </div>
                      </div>

                      {/* Clear Filters Button */}
                      {(topVotesWebsiteFilter !== 'all' || topVotesFromDate !== '2026-01-02T05:00' || topVotesToDate !== '2026-01-04T18:00') && (
                        <div className="mt-4">
                          <button
                            onClick={() => {
                              setTopVotesWebsiteFilter('all');
                              setTopVotesFromDate('2026-01-02T05:00');
                              setTopVotesToDate('2026-01-04T18:00');
                            }}
                            className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center space-x-2"
                          >
                            <i className="fas fa-times-circle"></i>
                            <span>Clear Filters</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      {loadingTopVotes ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
                        </div>
                      ) : (() => {
                        // Filter voters based on filters
                        // Backend already filters by streamer and date, just sort by total votes
                        let filteredVoters = topVotes;
                        
                        // Sort by total votes descending
                        filteredVoters.sort((a, b) => b.total_votes - a.total_votes);

                        return filteredVoters.length === 0 ? (
                          <div className="text-center py-12">
                            <i className="fas fa-inbox text-gray-600 text-5xl mb-4"></i>
                            <p className="text-gray-400">No voters found matching your filters</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-4">
                            {filteredVoters.map((voter, index) => (
                              <div 
                                key={voter.id}
                                className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:border-cyan-500 transition-all"
                              >
                                <div className="flex flex-col lg:flex-row gap-4">
                                  {/* Rank Badge */}
                                  <div className="flex-shrink-0 flex items-center justify-center lg:justify-start">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                                      index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white' :
                                      index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-white' :
                                      index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white' :
                                      'bg-gray-700 text-gray-300 border-2 border-gray-600'
                                    }`}>
                                      #{index + 1}
                                    </div>
                                  </div>

                                  {/* User Image */}
                                  <div className="flex-shrink-0">
                                    {voter.picture ? (
                                      <img
                                        src={voter.picture}
                                        alt={voter.name || voter.username || 'User'}
                                        className="w-full lg:w-32 h-32 object-cover rounded-lg"
                                      />
                                    ) : (
                                      <div className="w-full lg:w-32 h-32 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                                        <i className="fas fa-user text-white text-4xl"></i>
                                      </div>
                                    )}
                                  </div>

                                  {/* User Info */}
                                  <div className="flex-1 min-w-0 flex items-center">
                                    <div className="space-y-2 w-full">
                                      {/* Username or Name */}
                                      <div className="flex items-center text-gray-300">
                                        <i className="fas fa-at w-5 text-purple-400"></i>
                                        <span className="ml-2 font-medium">
                                          {voter.username ? 
                                            voter.username : 
                                            (voter.name ? 
                                              voter.name : 
                                              <span className="text-gray-500 italic">Username is not set</span>
                                            )
                                          }
                                        </span>
                                      </div>
                                      
                                      {/* Email */}
                                      <div className="flex items-center text-gray-300">
                                        <i className="fas fa-envelope w-5 text-pink-400"></i>
                                        <span className="ml-2 font-medium truncate">{voter.email}</span>
                                      </div>
                                      
                                      {/* Discord */}
                                      <div className="flex items-center text-gray-300">
                                        <i className="fab fa-discord w-5 text-indigo-400"></i>
                                        <span className="ml-2 font-medium">
                                          {voter.discord_name ? 
                                            voter.discord_name : 
                                            <span className="text-gray-500 italic">Discord is not set</span>
                                          }
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Total Votes */}
                                  <div className="flex-shrink-0 flex items-center justify-center">
                                    <div className="bg-gray-800 rounded-lg px-6 py-3 text-center min-w-[120px]">
                                      <div className="flex items-center justify-center space-x-2 mb-1">
                                        <i className="fas fa-heart text-pink-500"></i>
                                        <span className="text-white font-bold text-2xl">
                                          {voter.total_votes || 0}
                                        </span>
                                      </div>
                                      <p className="text-gray-400 text-xs">
                                        {topVotesWebsiteFilter !== 'all' ? 'Votes for ' + topVotesWebsiteFilter.replace('.com', '') : 'Total Votes'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Draw Tab */}
                {activeTab === 'draw' && (
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                          <h2 className="text-2xl font-bold text-white mb-2">Draw Participants</h2>
                          <p className="text-gray-400 text-sm">Select participants for the draw based on vote logs or approved uploads</p>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowDrawPriceModal(true)}
                            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 font-semibold shadow-lg"
                          >
                            <i className="fas fa-dollar-sign"></i>
                            <span>Set Draw Price</span>
                          </button>
                          <button
                            onClick={handleOpenWinnersModal}
                            className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 font-semibold shadow-lg"
                          >
                            <i className="fas fa-trophy"></i>
                            <span>Winners</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Nested Tabs: Vote | Upload */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      <div className="flex gap-2 mb-4 border-b border-gray-600 pb-4">
                        <button
                          onClick={() => setDrawSubTab('vote')}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                            drawSubTab === 'vote'
                              ? 'bg-cyan-600 text-white'
                              : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
                          }`}
                        >
                          <i className="fas fa-vote-yea mr-2"></i>
                          Vote
                        </button>
                        <button
                          onClick={() => setDrawSubTab('upload')}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                            drawSubTab === 'upload'
                              ? 'bg-cyan-600 text-white'
                              : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
                          }`}
                        >
                          <i className="fas fa-upload mr-2"></i>
                          Upload
                        </button>
                      </div>

                      {/* Vote Draw Content */}
                      {drawSubTab === 'vote' && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">From Date (created_at)</label>
                              <input
                                type="datetime-local"
                                value={drawFromDate}
                                onChange={(e) => setDrawFromDate(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">To Date (created_at)</label>
                              <input
                                type="datetime-local"
                                value={drawToDate}
                                onChange={(e) => setDrawToDate(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                              />
                            </div>
                            <div className="flex items-end">
                              <button
                                onClick={handleOpenSpinningWheel}
                                disabled={loadingDrawParticipants || drawParticipants.length === 0}
                                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors font-semibold flex items-center justify-center space-x-2"
                              >
                                <i className="fas fa-dice"></i>
                                <span>Draw ({drawParticipants.length})</span>
                              </button>
                            </div>
                          </div>
                          {/* Drop Zone checkboxes - below dates */}
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-300 mb-2">Drop Zone</label>
                            <p className="text-xs text-gray-400 mb-2">Leave unchecked for all drop zones, or select specific streamers</p>
                            <div className="flex flex-wrap gap-3">
                              {availableWebsites.map(website => (
                                <label key={website} className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={drawDropZoneFilter.includes(website)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setDrawDropZoneFilter(prev => [...prev, website]);
                                      } else {
                                        setDrawDropZoneFilter(prev => prev.filter(w => w !== website));
                                      }
                                    }}
                                    className="rounded bg-gray-700 border-gray-600 text-cyan-500 focus:ring-cyan-500"
                                  />
                                  <span className="text-sm text-gray-300">{website.replace(/\.com$/i, '')}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          {(drawFromDate || drawToDate || drawDropZoneFilter.length > 0) && (
                            <div className="mt-4">
                              <button
                                onClick={() => {
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  setDrawFromDate(today.toISOString().slice(0, 16));
                                  const endToday = new Date();
                                  endToday.setHours(23, 59, 59, 999);
                                  setDrawToDate(endToday.toISOString().slice(0, 16));
                                  setDrawDropZoneFilter([]);
                                }}
                                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors flex items-center space-x-2"
                              >
                                <i className="fas fa-times-circle"></i>
                                <span>Clear Filters</span>
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {/* Upload Draw Content */}
                      {drawSubTab === 'upload' && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">From Date (submitted_at)</label>
                              <input
                                type="datetime-local"
                                value={drawUploadFromDate}
                                onChange={(e) => setDrawUploadFromDate(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">To Date (submitted_at)</label>
                              <input
                                type="datetime-local"
                                value={drawUploadToDate}
                                onChange={(e) => setDrawUploadToDate(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                              />
                            </div>
                            <div className="flex items-end">
                              <button
                                onClick={handleOpenSpinningWheelUpload}
                                disabled={loadingDrawUploadParticipants || drawUploadParticipants.length === 0}
                                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors font-semibold flex items-center justify-center space-x-2"
                              >
                                <i className="fas fa-dice"></i>
                                <span>Draw ({drawUploadParticipants.length})</span>
                              </button>
                            </div>
                          </div>
                          {/* Drop Zone checkboxes - below dates */}
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-300 mb-2">Drop Zone</label>
                            <p className="text-xs text-gray-400 mb-2">Leave unchecked for all drop zones, or select specific streamers</p>
                            <div className="flex flex-wrap gap-3">
                              {availableWebsites.map(website => (
                                <label key={website} className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={drawDropZoneFilter.includes(website)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setDrawDropZoneFilter(prev => [...prev, website]);
                                      } else {
                                        setDrawDropZoneFilter(prev => prev.filter(w => w !== website));
                                      }
                                    }}
                                    className="rounded bg-gray-700 border-gray-600 text-cyan-500 focus:ring-cyan-500"
                                  />
                                  <span className="text-sm text-gray-300">{website.replace(/\.com$/i, '')}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          {(drawUploadFromDate || drawUploadToDate || drawDropZoneFilter.length > 0) && (
                            <div className="mt-4">
                              <button
                                onClick={() => {
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  setDrawUploadFromDate(today.toISOString().slice(0, 16));
                                  const endToday = new Date();
                                  endToday.setHours(23, 59, 59, 999);
                                  setDrawUploadToDate(endToday.toISOString().slice(0, 16));
                                  setDrawDropZoneFilter([]);
                                }}
                                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors flex items-center space-x-2"
                              >
                                <i className="fas fa-times-circle"></i>
                                <span>Clear Filters</span>
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Participants List */}
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
                      {drawSubTab === 'vote' ? (
                        loadingDrawParticipants ? (
                          <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mb-4"></div>
                            <p className="text-gray-400">Loading participants...</p>
                          </div>
                        ) : drawParticipants.length === 0 ? (
                          <div className="text-center py-12">
                            <i className="fas fa-inbox text-gray-600 text-5xl mb-4"></i>
                            <p className="text-gray-400">No participants found matching your filters</p>
                          </div>
                        ) : (
                          <div>
                            <div className="mb-4 flex items-center justify-between">
                              <p className="text-gray-300 font-semibold">
                                Total Participants: <span className="text-cyan-400">{drawParticipants.length}</span>
                              </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {drawParticipants.map((participant, index) => (
                                <div
                                  key={participant.email || index}
                                  className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:border-cyan-500 transition-all"
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-white font-bold text-lg">
                                        {(participant.name || participant.username || participant.email || 'U')[0].toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white font-semibold truncate">
                                        {participant.username || participant.name || 'Username not set'}
                                      </p>
                                      <p className="text-gray-400 text-sm truncate">{participant.email}</p>
                                      {participant.streamers && participant.streamers.length > 0 && (
                                        <p className="text-amber-400 text-xs mt-1 truncate" title={participant.streamers.join(', ')}>
                                          {participant.streamers.join(', ')}
                                        </p>
                                      )}
                                      <div className="flex items-center justify-between mt-2">
                                        <p className="text-cyan-400 text-xs font-semibold">
                                          {participant.vote_count} vote{participant.vote_count !== 1 ? 's' : ''}
                                        </p>
                                        {participant.latest_vote_date && (
                                          <p className="text-gray-400 text-xs">
                                            {new Date(participant.latest_vote_date).toLocaleString('en-US', {
                                              timeZone: 'Europe/Paris',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      ) : (
                        loadingDrawUploadParticipants ? (
                          <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mb-4"></div>
                            <p className="text-gray-400">Loading participants...</p>
                          </div>
                        ) : drawUploadParticipants.length === 0 ? (
                          <div className="text-center py-12">
                            <i className="fas fa-inbox text-gray-600 text-5xl mb-4"></i>
                            <p className="text-gray-400">No approved upload participants found matching your filters</p>
                          </div>
                        ) : (
                          <div>
                            <div className="mb-4 flex items-center justify-between">
                              <p className="text-gray-300 font-semibold">
                                Total Participants: <span className="text-cyan-400">{drawUploadParticipants.length}</span>
                              </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {drawUploadParticipants.map((participant, index) => (
                                <div
                                  key={participant.email || index}
                                  className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:border-cyan-500 transition-all"
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-white font-bold text-lg">
                                        {(participant.name || participant.username || participant.email || 'U')[0].toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white font-semibold truncate">
                                        {participant.username || participant.name || 'Username not set'}
                                      </p>
                                      <p className="text-gray-400 text-sm truncate">{participant.email}</p>
                                      {participant.streamers && participant.streamers.length > 0 && (
                                        <p className="text-amber-400 text-xs mt-1 truncate" title={participant.streamers.join(', ')}>
                                          {participant.streamers.join(', ')}
                                        </p>
                                      )}
                                      <div className="flex items-center justify-between mt-2">
                                        <p className="text-cyan-400 text-xs font-semibold">
                                          {participant.upload_count} upload{participant.upload_count !== 1 ? 's' : ''}
                                        </p>
                                        {participant.latest_upload_date && (
                                          <p className="text-gray-400 text-xs">
                                            {new Date(participant.latest_upload_date).toLocaleString('en-US', {
                                              timeZone: 'Europe/Paris',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Confirmation Modal */}
      {showApprovalModal && uploadToApprove && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
          onClick={handleModalBackdropClick}
        >
         <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 border border-gray-700 shadow-2xl">
           {/* Modal Header */}
           <div className="flex items-center space-x-3 mb-4">
             <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
               <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
               </svg>
             </div>
             <div>
               <h2 className="text-xl font-bold text-white">Confirmer l'approbation</h2>
               <p className="text-sm text-gray-400">Cette action ne peut pas être annulée</p>
             </div>
           </div>

           {/* Upload Details */}
           <div className="bg-gray-700 rounded-lg p-4 mb-6">
             <div className="flex items-start space-x-3">
               {uploadToApprove.image_url ? (
                 <img 
                   src={uploadToApprove.image_url} 
                   alt={uploadToApprove.title}
                   className="w-16 h-16 object-cover rounded-lg"
                 />
               ) : (
                 <div className="w-16 h-16 bg-gray-600 rounded-lg flex items-center justify-center">
                   <i className="fas fa-image text-gray-400"></i>
                 </div>
               )}
               <div className="flex-1 min-w-0">
                 <h3 className="font-semibold text-white text-sm">
                   <SecureText>{uploadToApprove.title}</SecureText>
                 </h3>
                 <p className="text-xs text-gray-400 mt-1">
                   Par <SecureText>{uploadToApprove.author}</SecureText>
                 </p>
                 <p className="text-xs text-gray-400">
                   {formatDate(uploadToApprove.submittedDate)}
                 </p>
               </div>
             </div>
           </div>

           {/* Confirmation Message */}
           <div className="mb-6">
             <p className="text-gray-300 text-center">
               Êtes-vous sûr de vouloir <span className="text-green-400 font-semibold">approuver</span> cet upload ?
             </p>
             <p className="text-sm text-gray-400 text-center mt-2">
               L'upload sera visible publiquement après approbation.
             </p>
           </div>

           {/* Action Buttons */}
           <div className="flex space-x-3">
             <button
               onClick={cancelApproval}
               className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
             >
               Annuler
             </button>
             <button
               onClick={confirmApproval}
               className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
             >
               Approuver
             </button>
           </div>
         </div>
       </div>
     )}

     {/* Rejection Confirmation Modal */}
     {showRejectionModal && uploadToReject && (
       <div 
         className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
         onClick={handleModalBackdropClick}
       >
        <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 border border-gray-700 shadow-2xl">
          {/* Modal Header */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Confirmer le rejet</h2>
              <p className="text-sm text-gray-400">Cette action ne peut pas être annulée</p>
            </div>
          </div>

          {/* Upload Details */}
          <div className="bg-gray-700 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              {uploadToReject.image_url ? (
                <img 
                  src={uploadToReject.image_url} 
                  alt={uploadToReject.title}
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-600 rounded-lg flex items-center justify-center">
                  <i className="fas fa-image text-gray-400"></i>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white text-sm">
                  <SecureText>{uploadToReject.title}</SecureText>
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  Par <SecureText>{uploadToReject.author}</SecureText>
                </p>
                <p className="text-xs text-gray-400">
                  {formatDate(uploadToReject.submittedDate)}
                </p>
              </div>
            </div>
          </div>

                     {/* Rejection Reason Dropdown */}
           <div className="mb-6">
             <label className="block text-gray-300 font-medium mb-3">Raison du rejet:</label>
             <select
               value={rejectionReason}
               onChange={(e) => setRejectionReason(e.target.value)}
               className="w-full bg-gray-600 border border-gray-500 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
             >
               <option value="">Sélectionnez une raison...</option>
               {rejectionReasons.map((reason, index) => (
                 <option key={index} value={reason.message}>
                   {reason.display}
                 </option>
               ))}
             </select>
           </div>

          {/* Confirmation Message */}
          <div className="mb-6">
            <p className="text-gray-300 text-center">
              Êtes-vous sûr de vouloir <span className="text-red-400 font-semibold">rejeter</span> cet upload ?
            </p>
            <p className="text-sm text-gray-400 text-center mt-2">
              L'upload ne sera plus visible après rejet.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={cancelRejection}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
            >
              Annuler
            </button>
                         <button
               onClick={confirmRejection}
               disabled={!rejectionReason}
               className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                 rejectionReason
                   ? 'bg-red-600 hover:bg-red-700 text-white'
                   : 'bg-gray-500 text-gray-400 cursor-not-allowed'
               }`}
             >
               Rejeter
             </button>
          </div>
                 </div>
       </div>
     )}

     {/* Image Popup Modal */}
     {showImageModal && selectedImage && (
       <div 
         className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
         onClick={closeImageModal}
       >
         <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
           {/* Close Button */}
           <button
             onClick={closeImageModal}
             className="absolute top-4 right-4 z-10 bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full transition-colors"
           >
             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
             </svg>
           </button>
           
           {/* Image Container */}
           <div className="relative" onClick={(e) => e.stopPropagation()}>
             {/* Image */}
             <img 
               src={selectedImage} 
               alt="Application submission" 
               className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl ${selectedImageBlur && !revealBlurredImage ? 'blur-xl' : ''}`}
             />
             
             {/* View Image Button - Only show for blurred images that haven't been revealed */}
             {selectedImageBlur && !revealBlurredImage && (
               <div className="absolute inset-0 flex flex-col items-center justify-center">
                 <button
                   onClick={() => setRevealBlurredImage(true)}
                   className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-2xl transition-all transform hover:scale-105 flex items-center space-x-3"
                 >
                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                   </svg>
                   <span>{t('viewImage')}</span>
                 </button>
                 <p className="mt-4 text-red-400 text-sm max-w-md text-center px-4">
                   <i className="fas fa-exclamation-triangle mr-2"></i>
                   {selectedImageBlur}
                 </p>
               </div>
             )}
           </div>
         </div>
       </div>
     )}

     {/* Vision Popup Modal */}
     {showVisionModal && selectedVision && (
       <div 
         className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
         onClick={closeVisionModal}
       >
         <div className="bg-gray-800 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-gray-700 shadow-2xl">
           {/* Header */}
           <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-700">
             <div className="flex items-center gap-3">
               <i className="fas fa-eye text-cyan-400 text-lg"></i>
               <h3 className="text-xl font-bold text-white">Vision</h3>
             </div>
             <button
               onClick={closeVisionModal}
               className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
             >
               <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
               </svg>
             </button>
           </div>
           
           {/* Content */}
           <div className="text-gray-200 leading-relaxed whitespace-pre-wrap">
             <MultilingualText text={selectedVision} fieldType="message" truncate={false} />
           </div>
         </div>
       </div>
     )}

     {/* About Popup Modal */}
     {showAboutModal && selectedAbout && (
       <div 
         className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
         onClick={closeAboutModal}
       >
         <div className="bg-gray-800 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-gray-700 shadow-2xl">
           {/* Header */}
           <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-700">
             <div className="flex items-center gap-3">
               <i className="fas fa-user text-purple-400 text-lg"></i>
               <h3 className="text-xl font-bold text-white">About</h3>
             </div>
             <button
               onClick={closeAboutModal}
               className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
             >
               <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
               </svg>
             </button>
           </div>
           
           {/* Content */}
           <div className="text-gray-200 leading-relaxed whitespace-pre-wrap">
             <MultilingualText text={selectedAbout} fieldType="about" truncate={false} />
           </div>
         </div>
       </div>
     )}

     {/* Delete Image Confirmation Modal */}
     {showDeleteImageModal && imageToDelete && (
       <div 
         className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
         onClick={(e) => {
           if (e.target === e.currentTarget) {
             cancelDeleteImage();
           }
         }}
       >
       <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 border border-gray-700 shadow-2xl">
         {/* Modal Header */}
         <div className="flex items-center space-x-3 mb-4">
           <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
             <i className="fas fa-trash text-white"></i>
           </div>
           <div>
             <h2 className="text-xl font-bold text-white">{t('confirmDeletion')}</h2>
             <p className="text-sm text-gray-400">{t('thisActionCannotBeUndone')}</p>
           </div>
         </div>

         {/* Image Details */}
         <div className="bg-gray-700 rounded-lg p-4 mb-6">
           <div className="flex items-start space-x-3">
             {imageToDelete.image_url ? (
               <img 
                 src={imageToDelete.image_url} 
                 alt={imageToDelete.title}
                 className="w-16 h-16 object-cover rounded-lg"
               />
             ) : (
               <div className="w-16 h-16 bg-gray-600 rounded-lg flex items-center justify-center">
                 <i className="fas fa-image text-gray-400"></i>
               </div>
             )}
             <div className="flex-1 min-w-0">
               <h3 className="font-semibold text-white text-sm">
                 <SecureText>{imageToDelete.title || t('untitled')}</SecureText>
               </h3>
               <p className="text-xs text-gray-400 mt-1">
                 {t('by')}: <SecureText>{imageToDelete.first_name} {imageToDelete.last_name}</SecureText>
               </p>
               <p className="text-xs text-gray-400">
                 {t('filterByWebsite')} <SecureText>{imageToDelete.website ? imageToDelete.website.replace('.com', '') : t('unknown')}</SecureText>
               </p>
             </div>
           </div>
         </div>

         {/* Confirmation Message */}
         <div className="mb-6">
           <p className="text-gray-300 text-center">
             {t('areYouSureDelete')}
           </p>
           <p className="text-sm text-gray-400 text-center mt-2">
             {t('imageWillBeRemoved')}
           </p>
         </div>

         {/* Action Buttons */}
         <div className="flex space-x-3">
           <button
             onClick={cancelDeleteImage}
             disabled={deletingImage}
             className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
           >
             {t('cancel')}
           </button>
           <button
             onClick={confirmDeleteImage}
             disabled={deletingImage}
             className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center"
           >
             {deletingImage ? (
               <>
                 <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                 {t('deleting')}
               </>
             ) : (
              t('delete')
            )}
          </button>
        </div>
      </div>
     </div>
   )}

    {/* Blur Reason Modal */}
    {blurModalUploadId && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
        onClick={() => setBlurModalUploadId(null)}
      >
        <div 
          className="bg-gray-800 rounded-xl w-[550px] max-w-[95vw] shadow-2xl border border-purple-500 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="bg-purple-600 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-700 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Select Blur Reason</h2>
                <p className="text-purple-200 text-sm">Choose why this image should be blurred</p>
              </div>
            </div>
            <button
              onClick={() => setBlurModalUploadId(null)}
              className="text-white hover:text-gray-200 p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Reason Options */}
          <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
            {blurReasons.map((reason, index) => (
              <button
                key={index}
                onClick={() => {
                  handleBlurReasonChange(blurModalUploadId, reason);
                  setBlurModalUploadId(null);
                }}
                className={`w-full text-left p-4 rounded-lg transition-all border-2 ${
                  blurReasonSelected[blurModalUploadId] === reason
                    ? 'bg-purple-600 bg-opacity-20 border-purple-500 text-purple-200'
                    : 'bg-gray-700 border-transparent hover:bg-gray-600 hover:border-purple-400 text-gray-200'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    blurReasonSelected[blurModalUploadId] === reason
                      ? 'border-purple-400 bg-purple-500'
                      : 'border-gray-500'
                  }`}>
                    {blurReasonSelected[blurModalUploadId] === reason && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm leading-relaxed">{reason}</span>
                </div>
              </button>
            ))}
          </div>
          
          {/* Footer with Cancel and Clear */}
          <div className="bg-gray-900 px-5 py-4 flex justify-between items-center">
            {blurReasonSelected[blurModalUploadId] ? (
              <button
                onClick={() => {
                  handleBlurReasonChange(blurModalUploadId, '');
                  setBlurModalUploadId(null);
                }}
                className="text-red-400 hover:text-red-300 text-sm font-medium flex items-center space-x-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Clear Selection</span>
              </button>
            ) : (
              <div></div>
            )}
            <button
              onClick={() => setBlurModalUploadId(null)}
              className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Draw Price Modal */}
    {showDrawPriceModal && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowDrawPriceModal(false);
            setDrawPrice('');
          }
        }}
      >
        <div className="bg-gray-800 rounded-xl p-6 max-w-4xl w-full mx-4 border border-gray-700 shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center">
                <i className="fas fa-dollar-sign text-white"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Manage Draw Prices</h2>
                <p className="text-sm text-gray-400">Add, edit, or delete prize amounts</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowDrawPriceModal(false);
                setDrawPrice('');
                setEditingPriceId(null);
                setEditingPriceValue('');
              }}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>

          {/* Add New Price Section */}
          <div className="mb-6 bg-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Add New Prize</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={drawPrice}
                onChange={(e) => setDrawPrice(e.target.value)}
                placeholder="Enter prize description (e.g., $100, iPhone 15, etc.)"
                className="flex-1 bg-gray-600 border border-gray-500 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && drawPrice && !isSavingDrawPrice) {
                    handleSaveDrawPrice();
                  }
                }}
              />
              <button
                onClick={handleSaveDrawPrice}
                disabled={isSavingDrawPrice || !drawPrice}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 whitespace-nowrap"
              >
                {isSavingDrawPrice ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus"></i>
                    <span>Add</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Existing Prices Table */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-300">Existing Prizes</h3>
              <button
                onClick={fetchDrawPrices}
                disabled={loadingDrawPrices}
                className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center space-x-1"
              >
                <i className={`fas fa-sync-alt ${loadingDrawPrices ? 'fa-spin' : ''}`}></i>
                <span>Refresh</span>
              </button>
            </div>

            {loadingDrawPrices ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mb-2"></div>
                <p className="text-gray-400 text-sm">Loading prices...</p>
              </div>
            ) : drawPrices.length === 0 ? (
              <div className="text-center py-8 bg-gray-700 rounded-lg">
                <i className="fas fa-inbox text-gray-500 text-3xl mb-2"></i>
                <p className="text-gray-400 text-sm">No prizes added yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Prize</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-gray-300 w-32">Status</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-gray-300 w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drawPrices.map((price) => (
                      <tr key={price.id} className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                        <td className="py-3 px-4">
                          {editingPriceId === price.id ? (
                            <input
                              type="text"
                              value={editingPriceValue}
                              onChange={(e) => setEditingPriceValue(e.target.value)}
                              className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                              autoFocus
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdateDrawPrice(price.id, editingPriceValue);
                                }
                              }}
                            />
                          ) : (
                            <span className="text-white text-sm">{price.price}</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => handleToggleDrawPriceStatus(price.id, price.status)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                              price.status === 1
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                            }`}
                          >
                            {price.status === 1 ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center space-x-2">
                            {editingPriceId === price.id ? (
                              <>
                                <button
                                  onClick={() => handleUpdateDrawPrice(price.id, editingPriceValue)}
                                  className="text-green-400 hover:text-green-300 transition-colors"
                                  title="Save"
                                >
                                  <i className="fas fa-check"></i>
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingPriceId(null);
                                    setEditingPriceValue('');
                                  }}
                                  className="text-gray-400 hover:text-gray-300 transition-colors"
                                  title="Cancel"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingPriceId(price.id);
                                    setEditingPriceValue(price.price);
                                  }}
                                  className="text-blue-400 hover:text-blue-300 transition-colors"
                                  title="Edit"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => {
                setShowDrawPriceModal(false);
                setDrawPrice('');
                setEditingPriceId(null);
                setEditingPriceValue('');
              }}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Spinning Wheel Modal */}
    {showSpinningWheel && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget && !isExecutingDraw) {
            setShowSpinningWheel(false);
            setSelectedPrize(null);
            setDrawWinner(null);
          }
        }}
      >
        <div className="bg-gray-800 rounded-xl p-8 max-w-2xl w-full mx-4 border border-gray-700 shadow-2xl max-h-[90vh] draw-modal-scroll">
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <i className="fas fa-trophy text-white text-xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Prize Draw ({drawType === 'upload' ? 'UPLOAD' : 'VOTES'})</h2>
                <p className="text-sm text-gray-400">Spin the wheel to select a prize and winner!</p>
              </div>
            </div>
            {!isExecutingDraw && (
              <button
                onClick={() => {
                  setShowSpinningWheel(false);
                  setSelectedPrize(null);
                  setDrawWinner(null);
                  setRecordedVideoUrl(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            )}
          </div>

          {/* Spinning Wheel */}
          <div className="flex flex-col items-center">
            <SpinningWheel
              ref={spinningWheelRef}
              prizes={activePrizes}
              drawType={drawType}
              onSpinStart={() => {
                // Reset states when new spin starts
                setRecordedVideoUrl(null);
                setIsGeneratingVideo(false);
                setDrawWinner(null);
                setDrawError(null);
              }}
              onSpinComplete={handleSpinComplete}
              disabled={isExecutingDraw}
              actualWinner={drawWinner}
              onRecordingComplete={(url, blob) => {
                console.log('Recording complete:', url);
                setRecordedVideoUrl(url);
                setIsGeneratingVideo(false);
              }}
            />

            {/* Winner Announcement - Compact */}
            {drawWinner && (
              <div className="mt-4 w-full bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <span className="text-2xl">🎉</span>
                  <h3 className="text-xl font-bold text-white">Winner!</h3>
                  <span className="text-2xl">🎉</span>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-3 backdrop-blur-sm">
                  <p className="text-white font-bold text-lg">
                    {drawWinner.name || drawWinner.username || 'Anonymous'}
                  </p>
                  <p className="text-white text-sm opacity-90">{drawWinner.email}</p>
                  <p className="text-white font-semibold text-sm mt-1">Prize: {drawWinner.prize}</p>
                </div>
                <p className="text-white text-xs mt-2 opacity-75">
                  <i className="fas fa-check-circle mr-1"></i>
                  Winner has been marked in the database
                </p>
                
                {/* Action Buttons - Download LEFT, Close RIGHT */}
                <div className="mt-3 flex items-center justify-center space-x-3">
                  {/* Video Download Button or Loader */}
                  {isGeneratingVideo && !recordedVideoUrl ? (
                    <div className="inline-flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg font-bold text-sm">
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating video...</span>
                    </div>
                  ) : recordedVideoUrl ? (
                    <a
                      href={recordedVideoUrl}
                      download={`prize-draw-${drawWinner.name || 'winner'}-${new Date().toISOString().slice(0, 10)}.webm`}
                      className="inline-flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-bold transition-colors text-sm"
                    >
                      <i className="fas fa-download"></i>
                      <span>Download Video</span>
                    </a>
                  ) : null}
                  <button
                    onClick={() => {
                      setShowSpinningWheel(false);
                      setSelectedPrize(null);
                      setDrawWinner(null);
                      setRecordedVideoUrl(null);
                      setIsGeneratingVideo(false);
                      if (drawType === 'upload') {
                        fetchDrawUploadParticipants();
                      } else {
                        fetchDrawParticipants();
                      }
                    }}
                    className="bg-white text-green-600 px-4 py-2 rounded-lg font-bold hover:bg-gray-100 transition-colors text-sm"
                  >
                    Close & Refresh
                  </button>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isExecutingDraw && !drawWinner && (
              <div className="mt-8 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-3"></div>
                <p className="text-gray-300 text-lg">Selecting random winner...</p>
              </div>
            )}

            {/* Draw Failed - Allow draw again */}
            {drawError && !isExecutingDraw && !drawWinner && (
              <div className="mt-4 w-full bg-red-600/20 border border-red-500/50 rounded-lg p-4 text-center">
                <p className="text-red-400 font-semibold mb-2">{drawError}</p>
                <p className="text-gray-400 text-sm mb-3">You can try again by clicking SPIN! above.</p>
                <button
                  onClick={() => setDrawError(null)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Info */}
            <div className="mt-6 bg-gray-700 rounded-lg p-4 w-full">
              <div className="flex items-start space-x-3">
                <i className="fas fa-info-circle text-cyan-400 mt-1"></i>
                <div className="text-sm text-gray-300">
                  <p className="font-semibold mb-1">How it works:</p>
                  <ol className="list-decimal list-inside space-y-1 text-gray-400">
                    <li>Click "SPIN THE WHEEL" to randomly select a prize</li>
                    <li>A random participant will be automatically selected as the winner</li>
                    <li>The winner will be marked in the database (draw = true)</li>
                    <li>Current participants: <span className="text-white font-bold">{drawType === 'upload' ? drawUploadParticipants.length : drawParticipants.length}</span></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Winners Modal */}
    {showWinnersModal && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowWinnersModal(false);
          }
        }}
      >
        <div className="bg-gray-800 rounded-xl p-6 max-w-5xl w-full mx-4 border border-gray-700 shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-600 to-orange-600 rounded-full flex items-center justify-center">
                <i className="fas fa-trophy text-white text-xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Draw Winners</h2>
                <p className="text-sm text-gray-400">All participants who have won a prize</p>
              </div>
            </div>
            <button
              onClick={() => setShowWinnersModal(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>

          {/* Winners Tabs: Vote | Upload */}
          {!loadingWinners && winners.length > 0 && (
            <div className="flex gap-2 mb-4 border-b border-gray-600 pb-4">
              <button
                onClick={() => setWinnersSubTab('vote')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  winnersSubTab === 'vote'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
                }`}
              >
                <i className="fas fa-vote-yea mr-2"></i>
                Vote ({voteWinners.length})
              </button>
              <button
                onClick={() => setWinnersSubTab('upload')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  winnersSubTab === 'upload'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
                }`}
              >
                <i className="fas fa-upload mr-2"></i>
                Upload ({uploadWinners.length})
              </button>
            </div>
          )}

          {/* Winners Table */}
          {loadingWinners ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mb-3"></div>
              <p className="text-gray-400 text-lg">Loading winners...</p>
            </div>
          ) : winners.length === 0 ? (
            <div className="text-center py-12 bg-gray-700 rounded-lg">
              <i className="fas fa-trophy text-gray-500 text-5xl mb-4"></i>
              <p className="text-gray-400 text-lg">No winners yet</p>
              <p className="text-gray-500 text-sm mt-2">Winners will appear here after draws are completed</p>
            </div>
          ) : displayedWinners.length === 0 ? (
            <div className="text-center py-12 bg-gray-700 rounded-lg">
              <i className="fas fa-trophy text-gray-500 text-5xl mb-4"></i>
              <p className="text-gray-400 text-lg">No {winnersSubTab} winners yet</p>
              <p className="text-gray-500 text-sm mt-2">Switch to the other tab to see winners</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">#</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Prize</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Draw Date</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedWinners.map((winner, index) => (
                    <tr 
                      key={`${winner.draw_id}-${winner.won_at}`} 
                      className="border-b border-gray-700 hover:bg-gray-700 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <span className="text-yellow-500 font-bold mr-2">
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏆'}
                          </span>
                          <span className="text-white font-semibold">{index + 1}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-white font-medium">
                          {winner.name || winner.username || 'Anonymous'}
                        </div>
                        {winner.username && winner.name && (
                          <div className="text-gray-400 text-xs">@{winner.username}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-300 text-sm">{winner.email}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white px-3 py-1 rounded-full text-sm font-semibold inline-block">
                          {winner.prize}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-400 text-sm">
                          {new Date(winner.won_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'Europe/Paris'
                          })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {winners.length > 0 && (
            <div className="mt-6 flex items-center justify-between pt-4 border-t border-gray-700">
              <div className="text-gray-400 text-sm">
                <i className="fas fa-info-circle mr-2"></i>
                {winnersSubTab === 'vote' ? 'Vote' : 'Upload'} Winners: <span className="text-white font-bold">{displayedWinners.length}</span>
                <span className="text-gray-500 ml-2">(Total: {winners.length})</span>
              </div>
              <button
                onClick={() => setShowWinnersModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    )}

  </div>
);
};

export default AdminDashboardOwner;